import type { Translations } from './de.js';

/**
 * Englische Texte. Vollständig gehalten, damit die Struktur nicht auseinanderläuft —
 * `Translations` erzwingt, dass kein Schlüssel fehlt.
 */
export const en: Translations = {
  app: {
    name: 'Kraftwerk',
    tagline: 'Training, nutrition and supplements — in one place.',
  },
  update: {
    available: 'A new version is ready.',
    required: 'This version is out of date. Your changes stay on this device until you update.',
    apply: 'Update now',
  },
  nav: {
    plan: 'Plan',
    today: 'Today',
    training: 'Training',
    nutrition: 'Nutrition',
    journal: 'Journal',
    household: 'Household',
    shopping: 'Shopping',
    costs: 'Costs',
    pocket: 'Pocket money',
    members: 'Members',
    meals: 'Meals',
    family: 'Family',
    community: 'Community',
  },
  rooms: {
    label: 'Spaces',
    switch: 'Switch space – now {{room}}',
    sheetTitle: 'Choose a space',
    sheetHint: 'Shortcut: swipe up on the space button.',
    close: 'Close',
    current: 'You are here',
    me: 'Me',
    household: 'Household',
    family: 'Family',
    community: 'Community',
    meHint: 'Your day: training, nutrition, journal, your own plans.',
    householdHint: 'Who lives here: shopping, costs, shared plans, members.',
    familyHint: 'People who help without living here — coming.',
    communityHint: 'From your area to all of Germany — coming.',
  },
  householdToday: {
    heading: 'Today in the household',
    membersHint: 'Invites, children, levels, location and family display',
    noHousehold: 'No household on this device yet — it arrives with the first sync.',
    events: 'Events today',
    noEvents: 'No household events today.',
    allDay: 'all day',
    forWhom: 'for {{names}}',
    tasks: 'Shared tasks today',
    noTasks: 'No shared task is due today.',
    everyone: 'For everyone',
    taskCount_one: '{{count}} task',
    taskCount_other: '{{count}} tasks',
    toPlan: 'Open plan',
    shopping: 'Shopping list',
    shoppingOpen_one: '{{count}} item open',
    shoppingOpen_other: '{{count}} items open',
    shoppingEmpty: 'Nothing on the list.',
    toList: 'Open list',
    stars: 'Stars',
    starsOf: '{{name}}: {{count}}',
    familyDisplay: 'Open family display',
  },
  familyRoom: {
    heading: 'Family',
    intro:
      'This is for people who don’t live with you but help out — grandparents who babysit, go along to appointments or cook now and then.',
    comingHeading: 'Coming',
    comingInvite:
      'Invite them with limited access: they only see what you share — never the journal, body data or costs.',
    comingEvents: 'Shared events: who babysits when, who goes along where.',
    comingTasks: 'Give tasks like “cook on Wednesday” to someone outside the household.',
    notYet: 'There is no function here yet — you can’t invite anyone.',
    meanwhileHeading: 'Until then',
    meanwhile:
      'Household events already have “for whom”: who is picked up or accompanied. Grandparents themselves can’t be added there yet.',
    toHouseholdPlan: 'Open the household plan',
  },
  communityRoom: {
    heading: 'Community',
    intro: 'Kraftwerk beyond your own household — from your area to all of Germany.',
    comingHeading: 'Coming first',
    comingRecipes:
      'Sharing recipes: show your own dishes and add other people’s dishes to your collection.',
    openNote:
      'Whatever is shared publicly is readable by everyone on purpose — unlike the rest of your data, which is encrypted. That needs moderation and a way to report content first.',
    notYet: 'Nothing is built here yet, and nothing leaves your account.',
  },
  login: {
    childOpen: 'For kids: sign in with a code from a parent’s phone',
    childCode: 'Code from a parent’s phone',
    childHint:
      'An adult in your household creates it under Household → Members. It is valid for 15 minutes.',
    childSubmit: 'Sign in',
    childFailed: 'The code is wrong or has expired. Ask for a new one.',
    intro: 'Sign in to continue.',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in …',
    failed: 'Email or password is incorrect.',
    offline: 'No connection to the server. Check your network and try again.',
    tooMany: 'Too many attempts. Please try again in a few minutes.',
    serverError: 'The server is not responding right now.',
    expired_one:
      'Your session has expired. 1 change is waiting on this device and will be sent after you sign in.',
    expired_other:
      'Your session has expired. {{count}} changes are waiting on this device and will be sent after you sign in.',
    passkey: 'Sign in with passkey',
    passkeyBusy: 'Waiting for your device to confirm …',
    passkeyFailed:
      "This passkey isn't registered here (anymore). Sign in with your password and set it up again in settings.",
    closedRegistration: 'Accounts are created on the server — there is deliberately no sign-up.',
  },
  session: {
    loading: 'One moment …',
    signedInChild:
      'Signed in as a child of this household. To sign in again you need a new code from a parent’s phone.',
    signedInAs: 'Signed in as {{email}}',
    logout: 'Sign out',
  },
  account: {
    heading: 'Account',
  },
  gestures: {
    pullHint: 'Pull to sync',
    pullRelease: 'Release to sync',
    undo: 'Undo',
    check: 'Tick off',
    uncheck: 'Reopen',
    done: 'Done',
    delete: 'Delete',
    deleted: '“{{name}}” deleted',
  },
  shortcuts: {
    heading: 'Shortcuts',
    intro:
      'Log weight and steps (also automatically from Apple Health) and tick off supplements without opening the app — with the iOS Shortcuts app (“Hey Siri, weight”). Every shortcut gets its own key that can do only these three things and read nothing.',
    add: 'Set up a shortcut',
    name: 'Name (to recognise it)',
    create: 'Create key',
    onlyOnce: 'Your key — shown only now. Copy it into the shortcut:',
    copy: 'Copy key',
    copied: 'Copied',
    done: 'Done',
    lastUsed: 'Last used on {{date}}',
    neverUsed: 'Never used',
    revoke: 'Revoke',
    revokeNamed: 'Revoke key “{{name}}”',
    step1:
      'Shortcuts app → new shortcut “Weight” → action “Ask for Input” (number, “Weight in kg?”).',
    step2: 'Action “Get Contents of URL”: {{url}} — method POST.',
    step3:
      'Under “Headers” add one row: key Authorization, value Bearer, a space and then your key. “Request Body” JSON, new field of type Number: kg = the input.',
    step4:
      'Action “Get Dictionary Value” (message) and “Show Result” — Siri then says what was logged.',
    step5: 'For “taken”, a second shortcut built the same way, without input, using {{url}}.',
    healthHeading: 'Apple Health: steps every evening, automatically',
    health1: 'Shortcuts app → Automation → “Time of Day”, say 22:00, “Run Immediately”.',
    health2:
      'Action “Find Health Samples”: type Steps, start date is today — then “Calculate Statistics”: Sum of Health Samples.',
    health3:
      'Action “Get Contents of URL”: {{url}}, POST, the same header (Authorization = Bearer and key). “Request Body” JSON, new field of type Number: steps = Statistics.',
    health4:
      'Weight from Health: “Find Health Samples” (Weight, latest) to {{url}} with kg = the value and date = its date (ISO 8601) — then yesterday’s scale reading lands on yesterday.',
    healthNote:
      'A day may be up to seven days back. Steps show in the journal; they do not count in the energy balance, which already includes everyday movement.',
  },
  quick: {
    likeYesterday_one: 'Like yesterday: take over 1 entry',
    likeYesterday_other: 'Like yesterday: take over {{count}} entries',
    recent: 'Recent',
    favorites: 'Favourites',
    onePortion: '1 portion',
    empty:
      'Nothing to add quickly yet — once you have logged something or marked a dish as favourite, it shows up here.',
    fromOff: 'from Open Food Facts — amount in grams below',
    incomplete:
      'Incomplete nutrition data — it cannot be logged like this. Create the food in the library and add the values from the package.',
    unpick: 'Other',
  },
  scan: {
    open: 'Scan barcode',
    preview: 'Camera view for scanning',
    hint: 'Hold the package barcode in view — it is detected automatically.',
    close: 'Stop scanning',
    slow: 'No code recognised yet. Move closer, hold it straight, good light — or type the digits under the barcode into the search field.',
    problem: {
      denied:
        'Camera access is not allowed. Enable the camera for Kraftwerk or Safari in iOS settings — or type the digits under the barcode.',
      noCamera: 'No camera found. Type the digits under the barcode.',
      failed: 'The camera could not be started. Type the digits under the barcode.',
    },
  },
  lookup: {
    sourceLabel: 'Source',
    source: { off: 'Package (Open Food Facts)', bls: 'Basic foods (BLS)' },
    queryBls: 'Food, e.g. “apple raw”',
    hintBls:
      'The German Food Composition Database (Max Rubner-Institut, CC BY 4.0) gives average values for unprocessed and typical foods — including vitamins and minerals. A hit only fills in the form.',
    query: 'Name or barcode',
    search: 'Search',
    busy: 'Searching …',
    hint: 'Open Food Facts is an open, user-maintained database. A result only fills the form — please compare with the package, then save.',
    none: 'Nothing found. Try another word or the barcode from the package.',
    per100g: 'per 100 g: {{kcal}} · {{protein}} protein',
    missing: 'n/a',
    results: 'Results',
    codeUnknown: 'Barcode {{code}} is not in Open Food Facts.',
    createWithCode: 'Create it yourself — with this barcode',
    incomplete: 'Incomplete — please add missing values from the package.',
    problem: {
      offline: 'Search needs a connection to the server.',
      tooMany: 'Too many searches. Please try again later.',
      unreachable: 'Open Food Facts is not responding right now.',
    },
  },
  import: {
    open: 'Get from a website',
    url: 'Link to the recipe',
    hint: 'Takes the recipe data the page provides for search engines — most recipe sites have it.',
    fetch: 'Fetch',
    busy: 'Fetching …',
    take: 'Take it',
    saving: 'Creating …',
    summary: '{{ingredients}} ingredients · {{steps}} steps',
    nutrition:
      'Nutrition from the page per portion: {{kcal}} kcal · {{protein}} g protein — taken over with its source. Once you match the ingredients to your foods, the dish calculates itself.',
    noNutrition:
      'Ingredients arrive as free lines without nutrition data. Match them to your foods afterwards — then the dish calculates.',
    problem: {
      blocked: 'This address cannot be fetched.',
      noRecipe: 'This page has no recipe data. We do not guess — please create the dish by hand.',
      unreachable: 'The page cannot be reached right now.',
      refused:
        "This site doesn't allow fetches from servers. Please add the dish by hand — or try another recipe site.",
      tooMany: 'Too many fetches. Please try again later.',
      offline: 'This needs a connection to the server.',
    },
  },
  cook: {
    prepareAsWritten: 'Get ready (amounts as written)',
    open_one: 'Cook · {{count}} portion',
    open_other: 'Cook · {{count}} portions',
    close: 'Finish',
    noWakeLock: 'This device cannot keep the screen on — it may turn off in between.',
    prepare_one: 'Get ready for {{count}} portion',
    prepare_other: 'Get ready for {{count}} portions',
    start: 'Let’s go',
    stepOf: 'Step {{number}} of {{total}}',
    previous: 'Back',
    next: 'Next',
    finish: 'Done cooking',
    howWasIt: 'How was it?',
    rating: 'Rating',
    note: 'For next time',
    noteHint: 'e.g. less salt, simmer longer',
    saveNote: 'Save note',
    skipNote: 'Finish without a note',
  },
  dish: {
    assign: 'Match {{name}}',
    assignHint: '“{{line}}” — which food is this?',
    assignConfirm: 'Match',
    duplicate: 'Create a variant',
    variantName: '{{name}} (variant)',
    share: 'Give to household',
    shareHint:
      'They get their own copy — including any missing foods. After that you are independent: changes do not carry over.',
    shareTo: 'Give to {{name}}',
    shared: '{{name}} now has a copy.',
    shareFailed: 'That did not work — it needs a connection to the server.',
    editing: 'Edit {{name}}',
    basics: 'Basics',
    saved: 'Saved.',
    done: 'Done',
    image: 'Picture',
    imageOffline:
      'The picture cannot be uploaded without a connection to the server. Please try again later.',
    imageTooLarge: 'The picture could not be made small enough.',
    imageFailed: 'The picture was not accepted. Please try again.',
    imageLocked: 'Pictures are only uploaded encrypted — unlock this device first.',
    imageBusy: 'Uploading …',
    imageChange: 'Change picture',
    imageAdd: 'Add picture',
    imageRemove: 'Remove picture',
    addHow: 'How to add?',
    moveUp: 'Move {{name}} up',
    moveDown: 'Move {{name}} down',
    changeAmount: 'Change amount of {{name}}',
    removeNamed: 'Remove {{name}}',
    amount: 'Amount',
    unit: 'Unit',
    equalsGrams: '= {{grams}} g',
    freeLine: 'Free line',
    freeLineHint: 'e.g. a pinch of salt',
    freeLineNote: 'Free lines carry no nutrition data and are not counted.',
    noSteps: 'No steps yet.',
    stepLabel: 'Step {{number}}',
    stepUp: 'Move step {{number}} up',
    stepDown: 'Move step {{number}} down',
    stepRemove: 'Remove step {{number}}',
    addStep: 'Add step',
    note: 'Note',
    sourceUrl: 'Source link',
    add: {
      food: 'From the library',
      newFood: 'New food',
      free: 'Free line',
    },
    search: 'Search dishes',
    filter: 'Filter by meal',
    allSlots: 'All',
    favorite: 'Favourite',
    noMatch: 'No dish matches.',
    gone: 'This dish no longer exists.',
    perPortion: 'Per portion',
    steps: 'Method',
    sourceLink: 'Recipe on {{host}}',
    notes: 'Cooking notes',
    ratingUp: 'good',
    ratingDown: 'not great',
    deleteWarning: 'The dish will be deleted. What you have already eaten stays in your log.',
    delete: 'Delete dish',
    back: 'Back',
    fewerPortions: 'One portion less',
    morePortions: 'One portion more',
    portions_one: '{{count}} portion',
    portions_other: '{{count}} portions',
    noIngredients: 'No ingredients recorded.',
    noNutrition: 'no nutrition data',
    actions: 'What you can do with this dish',
    eatToday: 'Eaten today',
    plan: 'Add to week plan',
    shop: 'Missing items to list',
    noHousehold: 'The shopping list needs a household first (Shopping tab).',
    addedToList_one: 'Added 1 item to the shopping list.',
    addedToList_other: 'Added {{count}} items to the shopping list.',
    nothingMissing: 'Nothing missing — all in stock or already on the list.',
    logged_one: 'Logged 1 portion.',
    logged_other: 'Logged {{count}} portions.',
    cannotLog: 'Without nutrition data this dish cannot be logged.',
    howMuch: 'How much?',
    logConfirm: 'Log it',
    planned: 'Planned for {{day}}.',
    day: 'Day',
    meal: 'Meal',
    planConfirm: 'Add to plan',
  },
  password: {
    change: 'Change password',
    changeHint:
      'All other devices will be signed out — this one stays signed in. Passkeys keep working.',
    current: 'Current password',
    next: 'New password (at least {{min}} characters)',
    repeat: 'Repeat new password',
    save: 'Change password',
    busy: 'Changing …',
    changed: 'Password changed. Other devices are signed out.',
    failed: 'That did not work — it needs a connection to the server.',
    problem: {
      tooShort: 'The new password needs at least {{min}} characters.',
      tooLong: 'The new password is too long.',
      mismatch: 'The two new passwords do not match.',
      unchanged: 'The new password is the same as the current one.',
    },
    show: 'Show password',
    hide: 'Hide password',
  },
  passkeys: {
    heading: 'Passkeys',
    intro:
      'Sign in without a password — confirmed with Face ID, fingerprint or the PIN of your device or password manager. The key stays there; the server only knows the public part.',
    none: 'No passkey set up yet.',
    offline: 'The list is only available while connected to the server.',
    add: 'Add passkey',
    name: 'Name (to recognise it)',
    defaultName: 'Passkey',
    namePlaceholder: 'e.g. iPhone',
    password: 'Password to confirm',
    confirm: 'Set up',
    busy: 'Waiting for your device to confirm …',
    exists: 'This device already has a passkey for your account.',
    failed: "That didn't work. Please try again.",
    remove: 'Delete',
    removeNamed: 'Delete passkey “{{name}}”',
    lastUsed: 'Last used on {{date}}',
    created: 'Set up on {{date}}',
  },
  crash: {
    heading: 'Something went wrong',
    screenHeading: 'This section could not be shown',
    safe: 'Your entries are safe on this device — nothing is lost. Reloading usually helps.',
    reload: 'Reload',
    details: 'Technical details',
  },
  theme: {
    heading: 'Appearance',
    system: 'Device',
    light: 'Light',
    dark: 'Dark',
  },
  sync: {
    done: 'Everything synced',
    syncing: 'Syncing …',
    pending_one: '1 change waiting',
    pending_other: '{{count}} changes waiting',
    offline_one: 'Offline · 1 change waiting',
    offline_other: 'Offline · {{count}} changes waiting',
    failed_one: '1 change rejected',
    failed_other: '{{count}} changes rejected',
    error: 'Sync failed',
    now: 'Now',
    retry: 'Try again',
    reason: 'Reason: {{reason}}',
    unreachable_one: 'Server unreachable · 1 change waiting on this device',
    unreachable_other: 'Server unreachable · {{count}} changes waiting on this device',
    serverError: 'Server error (HTTP {{status}}) — changes stay on this device',
    unreachableShort: 'Unreachable',
    updateRequired_one: 'New version needed · 1 change is waiting on this device.',
    updateRequired_other: 'New version needed · {{count}} changes are waiting on this device.',
    updateRequiredShort: 'Update',
    serverErrorShort: 'Server error {{status}}',
    heldEmpty_one: '1 change held back · no content',
    heldEmpty_other: '{{count}} changes held back · no content',
    heldEmptyShort: 'Held back',
    heldEmptyHint:
      'They would have emptied an entry on the server, so they stay on this device. Fill in the entry again and the new version goes out.',
  },
  supplements: {
    withMeal: 'With meal',
    withMealFor: '{{name}}: with meal',
    noMeal: 'not tied to a meal',
    atMeal: 'with {{slot}}',
    heading: 'Supplements',
    name: 'Name',
    time: 'Time',
    add: 'Add',
    remove: 'Delete',
    removeNamed: 'Delete {{name}}',
    empty: 'Nothing added yet.',
    noTime: 'no fixed time',
  },
  supplementLib: {
    intro:
      'Form, amount per dose (from the package) and when. You tick them off on the day and on “Today”.',
    new: 'New supplement',
    scan: 'Scan barcode',
    scanKnown: '{{name}} is already in your library.',
    scanFound: 'Found — please compare with the package.',
    scanUnknown: 'Not found. Enter the name from the package — the next scan will recognise it.',
    scanTooMany: 'Too many searches right now — the code is filled in anyway.',
    sourcePackage: 'Package',
    editNamed: 'Edit {{name}}',
    form: 'Form',
    forms: { powder: 'Powder', capsule: 'Capsule/tablet', liquid: 'Oil/liquid' },
    amount: 'Amount',
    unit: 'Unit',
    pickFormFirst: 'Pick the form first — it decides the unit.',
    amountInvalid: 'The amount is not a number.',
    noAmount: 'amount open',
    content: 'Content per unit (from the package)',
    contentExample: 'e.g. 1 capsule = 1000 IU vitamin D3',
    times: 'Times',
    timeNumber: 'Time {{number}}',
    removeTime: 'Remove {{time}}',
    addTime: 'Another time',
    weekdays: 'Weekdays',
    weekday: { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' },
    daily: 'daily',
    dailyHint: 'None picked: daily.',
    barcode: 'Barcode',
    barcodeInvalid: 'A barcode has 8 to 14 digits.',
    source: 'Source',
    save: 'Save',
  },
  acknowledge: {
    dueAt: 'Due at {{time}}',
    unknown: 'Intake',
    alreadyTaken: 'Already marked taken at {{time}}.',
    alreadySkipped: 'Skipped for today at {{time}}.',
    later: 'Just looking — back to the app',
    continue: 'Continue to the app',
  },
  today: {
    moreToday_one: '+1 more today',
    moreToday_other: '+{{count}} more today',
    hideRest: 'Hide the rest',
    heading: 'Due today',
    next: 'Due next',
    after: 'Then: {{name}} at {{time}}',
    nothingOpen: 'Nothing left open today.',
    empty: 'Nothing due today.',
    openCount_one: '1 open',
    openCount_other: '{{count}} open',
    take: 'Taken',
    takeNamed: 'Mark {{name}} as taken',
    skip: 'Not today',
    skipNamed: 'Skip {{name}} for today',
    done: 'Taken',
    skipped: 'Skipped today',
    ackFrom: 'check off from {{time}}',
    preparing: 'setting up …',
  },
  push: {
    heading: 'Notifications on this device',
    intro: 'So the app can reach you even when it is closed.',
    enable: 'Allow notifications',
    enabling: 'Setting up …',
    enabled: 'Notifications are active.',
    maxActions_one: 'This environment allows 1 action button in a notification.',
    maxActions_other: 'This environment allows {{count}} action buttons in a notification.',
    maxActionsUnknown:
      'This environment reports nothing about action buttons — normal on iOS. Tapping the notification always works.',
    denied: 'Notifications are blocked. This can only be changed in your device settings.',
    needsInstall:
      'On iPhone, notifications only work once the app is installed via Share → Add to Home Screen. Open it again from there.',
    unsupported: 'This browser cannot receive notifications.',
    notConfigured: 'Push is not set up on this server yet.',
    error: 'Setup failed: {{message}}',
  },
  status: {
    heading: 'System status',
    checking: 'Checking connection …',
    ok: 'Connected',
    degraded: 'Database not responding',
    offline: 'No server reachable',
    database: 'Database',
    retry: 'Check again',
  },
  training: {
    tabs: {
      label: 'Training area',
      overview: 'Overview',
      exercises: 'Exercises',
      plans: 'Plans',
      progress: 'Progress',
    },
    overview: {
      activePlan: 'Current plan',
      noActivePlan: 'No plan is set as current. Pick one or create a new one.',
      toPlans: 'Go to plans',
      planWithoutDays: 'This plan has no training days yet.',
      nextUp: 'Up next',
      start: 'Start',
      resume: 'Session running — continue',
      forgotten: '“{{title}}” has been running since {{date}} — forgot to finish?',
      forgottenWhy:
        'It ends at the last logged set, not now — otherwise the session would count for days.',
      forgottenEnd: 'Finish at the last set',
      forgottenResume: 'Keep going',
      freeSession: 'Start a free session',
      freeSessionHeading: 'Without a plan',
      freeSessionWhy: 'You pick exercises as you go.',
      freeSessionTitle: 'Free session',
      recent: 'Recent sessions',
      noSessions: 'No session recorded yet.',
      unfinished: 'not finished',
    },
    session: {
      startedAt: 'Started at {{time}}',
      finish: 'Finish session',
      noExercises: 'No exercise in this session yet. Add one below.',
      recent: 'Recently trained',
      addExercise: 'Add exercise',
      addExercisePlaceholder: 'Pick an exercise …',
      swapToday: 'Swap for today only',
      swapTodayWhy: 'Machine taken or something twinges? Your plan stays as it is.',
      swapTodayPlaceholder: 'Another exercise …',
      swappedFrom: 'Today instead of {{name}}',
      swapBack: 'Back to {{name}}',
      target: '{{sets}} sets · {{min}}–{{max}} reps',
      targetSetsOnly: '{{sets}} sets',
      details: 'Details',
      lastTime: 'Last time ({{date}}):',
      noHistory: 'No previous values — this is the first time.',
      warmup: 'Warm-up',
      superset: 'Superset — alternate, rest after the round',
      supersetNext: 'No rest — next up: {{name}}',
      deleteSet: 'Delete',
      deleteSetNamed: 'Delete set {{index}}',
      bodyWeightLabel: 'Body weight today (kg)',
      bodyWeightWhy:
        'Needed for pull-ups and dips, and stored with each set — otherwise every old estimate would become wrong later.',
      bodyWeightSave: 'Apply',
      suggestion: {
        increase: 'Suggestion: {{weight}} kg (+{{increment}}) — all sets hit the top last time.',
        hold: 'Suggestion: hold {{weight}} kg.',
        deload: 'Suggestion: {{weight}} kg — two sessions below the range.',
      },
    },
    set: {
      weight: 'Weight (kg)',
      addedWeight: 'Added (kg)',
      reps: 'Reps',
      seconds: 'Seconds',
      distance: 'Metres',
      side: 'Side',
      left: 'Left',
      right: 'Right',
      warmup: 'Warm-up set',
      log: 'Log set',
      less: 'Decrease {{field}}',
      more: 'Increase {{field}}',
    },
    rest: {
      running: 'Rest running',
      done: 'Rest is over.',
      skip: 'Skip',
      dismiss: 'Dismiss',
      pushOn: 'Your phone will notify you when the rest is over — even with the screen off.',
      pushOff:
        'Notifications are off: your phone will not tell you when the rest is over. Turn them on in Settings.',
      pushFailed:
        'No connection: no notification when the rest is over. The timer still runs — just not in your pocket.',
    },
    library: {
      muscles: 'Muscles',
      allMuscles: 'All',
      heading: 'Exercises',
      intro:
        'Your exercises — the starter set was copied when the account was created and is fully editable.',
      new: 'New exercise',
      search: 'Search',
      equipment: 'Equipment',
      anyEquipment: 'Any equipment',
      empty: 'No exercise found.',
      addVariant: 'Variant',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      deleteHint:
        'Deleting removes only this exercise. Variants below it stay — your history hangs on them.',
      variantOf: 'New variant of {{name}}',
      best: '≈ {{kg}} kg',
      sessions_one: '1 session',
      sessions_other: '{{count}} sessions',
      untrained: 'not trained yet',
    },
    detail: {
      back: 'Exercises',
      gone: 'This exercise no longer exists.',
      variantOf: 'Variant of {{name}}',
      newVariant: 'New variant',
      progress: 'Progress',
      noSets: 'No sets logged yet.',
      recent: 'Recent sessions',
      guide: 'Technique',
      noCues: 'No cues added.',
      variants: 'Variants',
    },
    art: {
      label: '{{name}}: start and end position',
      none: 'No picture stored.',
      approx: 'Similar variation.',
      credit: 'Drawing:',
      source: 'Everkinetic',
      license: 'CC BY-SA 4.0',
      modified: 'modified',
    },
    exercise: {
      name: 'Name',
      variantLabel: 'Short label',
      trackingMode: 'Tracking',
      equipment: 'Equipment',
      movementPattern: 'Movement pattern',
      bodyRegion: 'Region',
      primaryMuscles: 'Target muscles',
      secondaryMuscles: 'Also worked',
      defaultSets: 'Sets',
      repMin: 'Reps from',
      repMax: 'to',
      incrementKg: 'Step (kg)',
      restSeconds: 'Rest (s)',
      isCompound: 'Compound',
      isUnilateral: 'One-sided',
      isFavorite: 'Favourite',
      cues: 'Cues',
      cuesHint: 'One line per cue.',
      cuesDisclaimer: 'Short reminders, not technique coaching.',
      inheritedFrom: 'Cues from the base exercise {{name}}',
      setupNotes: 'Machine settings',
      setupPlaceholder: 'Machine 7, seat height 4, pin at 3',
      note: 'Note',
      videos: 'Videos',
      videoLabel: 'Label',
      videoUrl: 'Address (https)',
      videoStart: 'Start (s)',
      videoDefaultLabel: 'Technique',
      addVideo: 'Add video',
      removeVideoNamed: 'Remove video {{label}}',
      videoInvalid: 'Only https addresses are stored.',
      videoPrivacy:
        'Videos are linked, not embedded — no third-party code inside the app, and you see where it leads before tapping.',
      videoNeedsNetwork: 'Videos need a connection — unavailable with no signal.',
      noVideo: 'No video stored.',
      videoCount_one: '1 video stored',
      videoCount_other: '{{count}} videos stored',
      unknownHost: 'unknown source',
    },
    plans: {
      starterNew_one: 'There is a new plan template, such as Calisthenics 3×.',
      starterNew_other: 'There are {{count}} new plan templates, such as Calisthenics 3×.',
      starterFetch: 'Get new templates',
      starterFailed: 'The templates did not arrive. Please try again later.',
      heading: 'Plans',
      intro:
        'A plan holds training days, a training day holds exercises. Sets and range apply here only — the exercise itself stays untouched.',
      new: 'New plan',
      newPlan: 'Plan name',
      create: 'Create plan',
      empty: 'No plan created yet.',
      active: 'current',
      activate: 'Set as current',
      edit: 'Edit',
      delete: 'Delete',
      deleteNamed: 'Delete plan {{name}}',
      newDay: 'New training day',
      newDayPlaceholder: 'Upper A',
      addDay: 'Add day',
      noDays: 'No training day yet.',
      dayName: 'Training day name',
      deleteDay: 'Delete day',
      deleteDayNamed: 'Delete training day {{name}}',
      addExercise: 'Add exercise',
      pickExercise: 'Pick an exercise …',
      unknownExercise: 'Exercise not found',
      compound: 'Compound — stays first, never rotated',
      superset: 'Superset with the exercise above',
      sets: 'Sets',
      repMin: 'from',
      repMax: 'to',
      rest: 'Rest',
      moveUp: 'Move up',
      moveDown: 'Move down',
      removeEntry: 'Remove entry',
      swap: 'Swap',
      swapPlaceholder: 'Another exercise …',
      sameFamily: 'Same family',
      allExercises: 'All exercises',
    },
    progress: {
      historySummary:
        '{{name}}: estimated max from {{first}} to {{last}} kg over {{days}} training days',
      heading: 'Progress',
      streak: 'Consecutive weeks trained',
      streakValue_one: '{{count}} week',
      streakValue_other: '{{count}} weeks',
      streakWhy:
        'Consistency is what you can influence in week 3 — which is why it comes first here.',
      weeklyVolume: 'Weekly volume',
      sessions_one: '1 session',
      sessions_other: '{{count}} sessions',
      barLabel: 'Week of {{week}}: {{kg}} kg',
      uncounted_one: '1 set without volume in kg (time or distance) — not included.',
      uncounted_other: '{{count}} sets without volume in kg (time or distance) — not included.',
      oneRepMax: 'Estimated one-rep max',
      oneRepMaxWhy:
        'Epley estimate from weight and reps. For comparison with yourself, not as a record.',
      estimate: '{{best}} kg · {{sets}} sets',
      noEstimate: 'no estimate possible',
    },
    mode: {
      weightReps: 'Weight × reps',
      bodyweightReps: 'Body weight × reps',
      weightedBodyweight: 'Body weight + added',
      timeHold: 'Timed hold',
      distanceTime: 'Distance and time',
    },
    equipment: {
      barbell: 'Barbell',
      dumbbell: 'Dumbbell',
      machine: 'Machine',
      cable: 'Cable',
      bodyweight: 'Body weight',
      kettlebell: 'Kettlebell',
      band: 'Band',
      other: 'Other',
    },
    pattern: {
      push: 'Push',
      pull: 'Pull',
      squat: 'Squat',
      hinge: 'Hinge',
      lunge: 'Lunge',
      core: 'Core',
      carry: 'Carry',
      cardio: 'Cardio',
      isolation: 'Isolation',
    },
    region: {
      upper: 'Upper body',
      lower: 'Lower body',
      core: 'Core',
      fullBody: 'Full body',
    },
    muscleGroup: {
      chest: 'Chest',
      back: 'Back',
      shoulders: 'Shoulders',
      arms: 'Arms',
      legs: 'Legs',
      core: 'Core',
      cardio: 'Cardio',
    },
    muscle: {
      chest: 'Chest',
      back: 'Back',
      lats: 'Lats',
      shoulders: 'Shoulders',
      rearDelts: 'Rear delts',
      biceps: 'Biceps',
      triceps: 'Triceps',
      forearms: 'Forearms',
      quads: 'Quads',
      hamstrings: 'Hamstrings',
      glutes: 'Glutes',
      calves: 'Calves',
      core: 'Core',
      lowerBack: 'Lower back',
      cardio: 'Cardio',
    },
  },
  nutrition: {
    tabs: {
      label: 'Nutrition area',
      day: 'Day',
      week: 'Week',
      library: 'Library',
    },
    macroShort: {
      kcal: 'kcal',
      protein: 'Protein',
      carbs: 'Carbs',
      fat: 'Fat',
    },
    macro: {
      kcal: 'Calories',
      protein: 'Protein',
      carbs: 'Carbs',
      fat: 'Fat',
    },
    slot: {
      breakfast: 'Breakfast',
      lunch: 'Lunch',
      pre: 'Pre-workout',
      post: 'Post-workout',
      dinner: 'Dinner',
      snack: 'Snack',
      snack2: 'Snack 2',
      snack3: 'Snack 3',
      snack4: 'Snack 4',
    },
    day: {
      supplementsAsk: 'Taken with it?',
      supplementsAt: 'Supplements',
      supplementTaken: 'taken',
      supplementSkipped: 'not today',
      supplementTakeNamed: '{{name}} taken',
      heading: 'Daily log',
      today: 'today',
      previous: 'Previous day',
      next: 'Next day',
      slotEmpty: 'nothing logged',
      change: 'Change',
      changePlannedNamed: 'Change amount of {{name}}',
      plannedPortionsShort_one: '1 portion',
      plannedPortionsShort_other: '{{amount}} portions',
      fromPlan: 'planned',
      skipPlannedNamed: 'Skip {{name}}',
      plannedSkipped: 'skipped',
      notCounted: 'not counted',
      count: 'Count',
      restorePlannedNamed: 'Count {{name}} again',
      plannedWithoutValues:
        'Without nutrition values (or an amount) it can’t count — add them to the dish or food.',
      macrosHeading: 'Day status',
      plannedUnknown: 'Entry without a dish',
      unplanNamed: 'Remove {{name}} from the plan',
      plannedGrams: '{{name}}, {{grams}} g',
      plannedPortions: '{{name}}, {{count}} portions',
      plan: 'Plan',
      add: 'Add',
      log: 'Eaten',
      removeNamed: 'Remove {{name}} from the log',
      choose: 'Choose …',
      pickRecipe: 'Dish',
      pickFood: 'Food',
      freeLabel: 'What was it?',
      freeHint: 'For eating out — an estimate beats no entry at all.',
      recipeWithoutMacros:
        'This dish has no nutrition values. Enter them on the dish or add ingredients — an entry with 0 kcal would look like a statement.',
      photo: {
        start: 'Estimate from photo',
        pick: 'Take or choose a photo',
        consentHeading: {
          anthropic: 'Send the photo to an AI service?',
          ollama: 'Have your server estimate the photo?',
        },
        consent: {
          anthropic:
            'To estimate, this one photo goes to an AI service (Anthropic). It processes the photo only for the estimate; Kraftwerk does not store it. This path bypasses end-to-end encryption.',
          ollama:
            'The photo is estimated on your own server and not stored; it never leaves the server. For this, the server briefly sees this one photo — bypassing end-to-end encryption.',
        },
        consentResult:
          'The estimate runs in the background, usually 1–3 minutes — you can leave the app meanwhile. The result is an estimate and can be far off; nothing is logged until you have checked it.',
        send: 'Estimate it',
        cancel: "Don't send",
        preparing: 'Shrinking the photo …',
        busy: 'Sending …',
        resultHeading: 'Suggestion from the photo (AI)',
        resultHint: 'Can be far off — please check and adjust.',
        today: {
          heading: 'Photo estimate',
          waiting_one: '{{count}} photo is being estimated …',
          waiting_other: '{{count}} photos are being estimated …',
          ready_one: '{{count}} suggestion is waiting for you.',
          ready_other: '{{count}} suggestions are waiting for you.',
          failed_one: '{{count}} estimate didn’t work.',
          failed_other: '{{count}} estimates didn’t work.',
          open: 'Go to day',
        },
        pending: {
          heading: 'Estimating the photo …',
          hint: 'Usually 1–3 min. You can leave the app — a notification will tell you.',
          unsent: 'Waiting for a connection — sent as soon as the server is reachable.',
          queued: 'Number {{position}} in the queue.',
          running: 'Being estimated right now.',
          locked:
            'The suggestion has arrived, but encrypted. Unlock encryption on this device and it will show up here.',
          failedHeading: 'The estimate did not work',
          noPhoto: 'The photo is on the device that sent it — sending again only works there.',
          dish: 'Dish: {{dish}}',
          values: '{{kcal}} kcal · {{protein}} g protein · {{carbs}} g carbs · {{fat}} g fat',
          accept: 'Looks right',
          adjust: 'Adjust',
          discard: 'Discard',
          resend: 'Send again',
          save: 'Log it like this',
          slotOpen: 'Estimate pending',
          atLeast: 'at least',
          dayOpen_one: 'Includes {{count}} pending estimate — the total is at least this high.',
          dayOpen_other: 'Includes {{count}} pending estimates — the total is at least this high.',
        },
        portion: 'Assumed: {{portion}}',
        confidence: {
          low: 'Confidence: low',
          medium: 'Confidence: medium',
          high: 'Confidence: high',
        },
        discard: 'Discard estimate',
        badge: 'estimated (photo, AI)',
        note: 'Assumed: {{portion}}',
        dayContains: 'Includes photo estimates (AI) — the total is approximate.',
        error: {
          offline: 'No connection — the estimate needs the server.',
          disabled: 'Photo estimates are not set up on this server.',
          child: 'Photo estimates are not available for child accounts.',
          not_food: 'No food recognised in the photo. Try another photo?',
          refused: 'The AI service declined the estimate. Please estimate yourself.',
          invalid: 'The AI service sent an unusable answer. Try again or estimate yourself.',
          upstream: 'The AI service is not reachable right now. Try again later.',
          rate: 'Enough photos estimated for today. Please log it yourself.',
          tooLarge: 'The photo is too large.',
          photo: 'The photo could not be opened.',
          unknown: 'The estimate failed.',
          aborted:
            'The server restarted before the photo’s turn — it was only held in memory. Please send it again.',
          busy: 'Too many photos are waiting right now. Please send it again later.',
          identity:
            'The key pair for the encrypted result is still missing on this account. Unlock encryption and send again.',
        },
      },
      mode: {
        quick: 'Quick',
        recipe: 'Dish',
        food: 'Food',
        free: 'Free',
      },
    },
    week: {
      heading: 'Week plan',
      otherSlots: 'Other slots',
      intro:
        'The next seven days. Whatever you plan here or on the day shows up in both places and goes onto the shopping list via “From the week plan”. Planned meals count as eaten on the day — skip there whatever didn’t happen.',
      add: 'Plan',
      plan: 'Add',
      empty: 'Nothing planned.',
      remove: 'Remove from the plan',
      supplements: 'Supplements: {{list}}',
    },
    library: {
      searchFoods: 'Search',
      noFoodMatch: 'No food matches the search.',
      foodLine: '{{kcal}} kcal · P {{protein}} · C {{carbs}} · F {{fat}} g per 100 g',
      heading: 'Library',
      recipes: 'Dishes',
      foods: 'Foods',
      supplements: 'Supplements',
      recipesIntro:
        'Your standard dishes. The starter set comes from the prototype — every row is editable.',
      foodsIntro:
        'Values per 100 g, exactly as printed on the packaging. Dishes compute from these.',
      newRecipe: 'New dish',
      newFood: 'New food',
      noRecipes: 'No dish created yet.',
      noFoods:
        'No food created yet. The app deliberately ships none — invented values would look like verified data.',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      deleteNamed: 'Delete {{name}}',
    },
    recipe: {
      name: 'Name',
      slot: 'Slot',
      portions: 'Portions',
      items: 'Ingredient line',
      prepNote: 'Prepare the evening before',
      prepNoteHint: 'e.g. soak the oats',
      prepNoteShown: 'Evening before: {{note}}',
      ingredients: 'Ingredients',
      noIngredients: 'No ingredients — the entered values apply.',
      ingredientsWin:
        'This dish computes from its ingredients. Entered values apply again once no ingredient is left.',
      needFoods: 'Computing needs foods in the library first.',
      food: 'Food',
      pickFood: 'Choose …',
      grams: 'Grams',
      addIngredient: 'Add',
      removeIngredient: 'Remove ingredient',
      unknownFood: 'Food deleted',
      fromIngredients: 'computed from ingredients',
      entered: 'entered',
      noMacros: 'No nutrition values stored',
      source: 'Source: {{note}}',
      kcalMismatch:
        'The macros add up to {{computed}} kcal. Small deviations are normal — larger ones are usually a typo.',
    },
    food: {
      portionName: 'Portion (name)',
      portionNameHint: 'e.g. slice',
      portionGrams: 'Grams per portion',
      name: 'Name',
      brand: 'Brand',
      per100g: 'per 100 g',
      per100gHint: 'All values per 100 g — exactly as printed on the packaging.',
      findHeading: 'Find a food',
      manual: 'Enter it yourself',
      findAgain: 'Search again',
      byHand: 'By hand — copy the values from the packaging.',
      withCode: 'Barcode {{code}} is saved with it — next time you scan, it is there.',
      fromSource: {
        off: 'From Open Food Facts — please compare with the packaging, then save.',
        bls: 'From the BLS (averages) — adjust if needed, then save.',
      },
      source: 'Source',
      sourcePackage: 'Packaging',
      noSource: 'No source given.',
      category: 'Shop aisle',
    },
    targets: {
      heading: 'No targets yet',
      intro:
        'Without a target the app only adds things up. It suggests personal targets once it knows your height, weight, age, daily activity and goal.',
      personal: 'Enter your details',
      manual: 'Enter your own targets',
      save: 'Apply',
      noProfile: 'No profile on this device — please sync first.',
      teenIntro:
        'You set your own targets — for example with your coach. Kraftwerk does not suggest values for teens.',
    },
    rules: {
      heading: 'Rules of thumb',
      source: 'General guidelines, not a prescription — the app only logs.',
    },
  },
  review: {
    trends: 'Trend · {{count}} weeks',
    trendWeight: 'Weight (weekly average)',
    trendProtein: 'Protein (avg per day)',
    trendVolume: 'Training volume',
    trendSupplements: 'Supplements taken',
    trendSummary: '{{label}}: from {{first}} to {{last}}, {{weeks}} weeks with data',
    trendNone: '{{label}}: no data yet',
    trendTooFew: 'Not enough weeks with data for a line yet.',
    range: 'Week {{from}} – {{to}}',
    nutrition: 'Food',
    noFood: 'Nothing logged this week — no data.',
    avgProtein: 'g protein on average',
    avgKcal: 'Ø {{kcal}} kcal',
    daysLogged_one: 'logged on {{count}} day',
    daysLogged_other: 'logged on {{count}} days',
    proteinGoal_one: 'Protein goal reached on {{count}} day',
    proteinGoal_other: 'Protein goal reached on {{count}} days',
    training: 'Training',
    sessions_one: 'session',
    sessions_other: 'sessions',
    previous: 'previous week {{value}}',
    volume: '{{kg}} kg volume',
    supplements: 'Supplements',
    noIntakes: 'No intakes were due.',
    taken: 'taken ({{taken}} of {{due}})',
    skipped_one: '{{count}} skipped',
    skipped_other: '{{count}} skipped',
    weight: 'Weight',
  },
  coach: {
    heading: 'AI coach',
    intro:
      'An AI model looks at your numbers from the last weeks and replies with observations and questions. You see exactly what is sent beforehand.',
    open: 'Talk through the weeks with the AI coach',
    weeks: 'Period',
    weekOption_one: '{{count}} week',
    weekOption_other: '{{count}} weeks',
    noData: 'There are too few entries for this period yet — nothing to talk about.',
    notice: {
      heading: {
        ollama: 'This goes to your server and is not stored',
        anthropic: 'This goes through your server to Anthropic',
      },
      body: {
        ollama:
          'The summary below goes to your own server, where the model runs. It does not leave the server and is not stored. To answer, the server reads it briefly — bypassing the encryption.',
        anthropic:
          'The summary below goes through your server to Anthropic, an outside service. Your server does not store it; what Anthropic does with it is governed by its terms. To answer, it is readable — bypassing the encryption.',
      },
    },
    preview: {
      range: 'Period {{from}} – {{to}}',
      none: 'not recorded',
      training: 'Training',
      sessions_one: '{{count}} session · per week {{perWeek}}',
      sessions_other: '{{count}} sessions · per week {{perWeek}}',
      plan: 'Planned {{planned}} · done {{done}} · missed {{missed}} · cancelled {{cancelled}}',
      noPlan: 'No training days planned — nothing recorded about missed sessions',
      missedBy: 'Missed by weekday: {{list}}',
      trainingDays: 'Your training days: {{list}}',
      noTrainingDays: 'No fixed training days',
      volume: 'Volume per week (kg × reps): {{list}}',
      exercises: 'Exercises (sessions): {{list}}',
      noExercises: 'No exercises with sets',
      rating: 'Your own session rating avg. {{value}}',
      nutrition: 'Food and water',
      daysLogged: 'Logged on {{logged}} of {{days}} days',
      avgFood: 'Avg. {{kcal}} kcal · avg. {{protein}} g protein (on logged days)',
      targets:
        'Your target {{kcal}} kcal · {{protein}} g protein · protein target met on {{days}} days',
      noTargets: 'No targets set',
      water:
        'Water logged on {{logged}} days · avg. {{avg}} ml · target {{target}} ml · met on {{days}} days',
      supplements: 'Supplements',
      intakes: 'Due {{due}} · taken {{taken}} · skipped {{skipped}} (without names)',
      noIntakes: 'Nothing due',
      journal: 'Wellbeing',
      journalDays_one: 'Wellbeing logged on {{count}} day',
      journalDays_other: 'Wellbeing logged on {{count}} days',
      sleep: 'Sleep avg. {{hours}} h · sleep quality avg. {{quality}} (1–5)',
      feeling: 'Energy avg. {{energy}} · mood avg. {{mood}} · soreness avg. {{soreness}} (1–5)',
      never:
        'Not included: thoughts and notes, photos, weight and measurements, supplement names and doses, appointments and other people.',
      raw: 'Exactly what is sent',
    },
    send: 'Send and talk it through',
    busy: 'The coach is thinking …',
    wait: 'This can take a few minutes. Keep the app open meanwhile.',
    cancel: "Don't send",
    close: 'Close',
    result: {
      heading: 'What the coach notices',
      label: 'Suggestion from an AI model, may be off.',
      questions: 'To think about',
      suggestion: 'A suggestion',
      removed_one:
        '{{count}} sentence of the reply was hidden because it sounded like a diagnosis, dose, target or judgement.',
      removed_other:
        '{{count}} sentences of the reply were hidden because they sounded like a diagnosis, dose, target or judgement.',
      notStored: 'The reply is not stored — closing means it is gone.',
    },
    care: 'If something is weighing on you or you have not felt well for a while, talk to a doctor. The app cannot judge that.',
    note: {
      title: 'AI coach',
      save: 'Keep as a thought',
      saved: 'Kept as a thought',
      failed: 'Keeping it did not work. Please try again.',
    },
    error: {
      disabled: 'The AI coach is not set up on this server.',
      child: 'The AI coach is not available for children.',
      summary: 'The summary does not match the server — please reload the app.',
      refused: 'The model declined to answer.',
      invalid: 'The model’s reply was unusable. Please try again later.',
      unsafe:
        'The model’s reply failed the check (diagnosis, dose, target or judgement) and is not shown.',
      upstream: 'The model is not reachable or too slow right now. Please try again later.',
      rate: 'Enough conversations for today. Try again tomorrow.',
      offline: 'No connection to the server.',
      unknown: 'That did not work. Please try again later.',
    },
  },
  energy: {
    teenHeading: 'My targets',
    teenIntro:
      'You set calories and protein yourself. Kraftwerk does not calculate anything from weight or height for teens — those formulas are for adults.',
    selfSet: 'Source: set by you',
    heading: 'Body and energy',
    intro:
      'The app estimates your basal rate and needs from these. Exercise is added separately so nothing counts twice.',
    birthYear: 'Year of birth',
    heightCm: 'Height (cm)',
    sex: 'Formula',
    sexOption: { male: 'male', female: 'female' },
    sexWhy: 'The basal rate formula has two variants — nothing else.',
    activity: 'Everyday life without exercise',
    activityWhy: 'Training and activities from the calendar are added separately.',
    level: {
      sedentary: 'Mostly sitting (office)',
      light: 'Sitting, some walking or standing',
      onFeet: 'On your feet a lot (retail, trades)',
      heavy: 'Physically hard work',
    },
    goal: 'Goal',
    goalOption: { lose: 'Lose weight', maintain: 'Maintain', gain: 'Build up' },
    rate: '% body weight per week',
    notSet: 'Not set',
    missing: 'Still missing for the estimate: {{list}}.',
    lastWeight: 'Last weighed: {{kg}} kg — leave today empty if nothing changed.',
    weightWhy: 'Saved as today’s weigh-in — the history is in the journal.',
    missingField: {
      birthYear: 'year of birth',
      sex: 'formula',
      heightCm: 'height',
      weightKg: 'Weight',
      activityLevel: 'everyday life',
    },
    bmr: 'Basal rate ≈ {{kcal}} kcal',
    typical: 'Needs on a typical day with your training ≈ {{kcal}} kcal',
    noGoal: 'Pick a goal and the app suggests targets.',
    suggestion:
      'Suggestion: {{kcal}} kcal · {{protein}} g protein · {{carbs}} g carbs · {{fat}} g fat',
    delta: {
      lose: '{{kcal}} kcal per day below your needs',
      maintain: 'About your needs',
      gain: '{{kcal}} kcal per day above your needs',
    },
    take: 'Use as targets',
    taken: 'Done.',
    isCurrent: 'These are already your targets.',
    estimate: 'An estimate, not a measurement. Sources:',
    currentTargets: 'Targets: {{kcal}} kcal · {{protein}} g P · {{carbs}} g C · {{fat}} g F',
    noTargets: 'No targets yet.',
    editTargets: 'Change',
    saveTargets: 'Save targets',
    dayHeading: 'Balance',
    dayNeed: 'Needs ≈ {{kcal}} kcal',
    dayDone: 'incl. training {{kcal}} kcal',
    dayPlanned: 'incl. expected {{kcal}} kcal (planned)',
    dayEaten: 'eaten {{kcal}} kcal',
    dayBalance: 'Balance {{sign}}{{kcal}} kcal',
    daySetup: 'Details are missing for a balance — Settings → Body and energy.',
  },
  schedule: {
    heading: 'Next training sessions',
    setup: 'Training days',
    done: 'Done',
    empty: 'No training days yet. Set which weekdays you go.',
    noneSoon: 'No training in the next two weeks.',
    more_one: '1 more session',
    more_other: '{{count}} more sessions',
    fewer: 'Show fewer',
    noPlanDay: 'no plan day',
    extra: 'extra',
    trained: 'Trained',
    cancelled: 'Skipped',
    missed: 'Missed',
    date: 'Day',
    time: 'Time',
    move: 'Move',
    cancel: 'Skip',
    cancelNamed: 'Skip training {{day}}',
    moveNamed: 'Move training {{day}}',
    restore: 'Don’t skip',
    remove: 'Remove',
    removeSlot: 'Remove {{day}} {{time}}',
    rotationWhy:
      'Which days you go. The plan day rotates in order — if a session is cancelled, its plan day comes next time.',
    weekday: 'Weekday',
    duration: 'Duration (min)',
    addSlot: 'Add training day',
    reminder: 'Reminder "Leave at …"',
    reminderOn: 'Reminder on',
    reminderOff: 'Reminder off',
    travel: 'Travel (min)',
    lead: 'Lead (min)',
    reminderWhy:
      'The notification arrives travel + lead time before the start. Tapping it acknowledges it; it stays quiet on days you trained or cancelled.',
    addExtra: 'Extra session',
    add: 'Add',
    today: 'Training today',
    todayLine: '{{time}} · {{name}}',
    leaveAt: 'Leave at {{time}}',
    toTraining: 'Go to training',
    quick: {
      today: 'Today {{time}}',
      tomorrow: 'Tomorrow {{time}}',
      day: '{{day}} {{time}}',
      other: 'Another day',
    },
    missedHeading: 'Training missed',
    missedLine: '{{day}} · {{time}}',
    missedWaiting: '{{name}} waits for your next training — nothing gets dropped.',
    missedWaitingNoPlan: 'Catch up or skip — your plan continues where you left off.',
    catchUp: 'Catch up',
    catchUpNamed: 'Catch up training {{day}}',
    reminderEnable: 'Turn on reminder before training',
  },
  activity: {
    strength: 'Strength training',
    walking: 'Walking',
    hiking: 'Hiking',
    running: 'Running',
    cycling: 'Cycling',
    swimming: 'Swimming',
    yoga: 'Yoga',
    gardening: 'Gardening',
    football: 'Football',
    dancing: 'Dancing',
  },
  calendar: {
    heading: 'Calendar',
    view: 'View',
    mode: { agenda: 'List', week: 'Week', month: 'Month' },
    previous: { week: 'Previous week', month: 'Previous month' },
    next: { week: 'Next week', month: 'Next month' },
    new: 'New event',
    earlier: 'Show earlier events',
    later: 'Another 30 days',
    empty: 'No events in this period.',
    forWhom: 'for {{names}}',
    connectHint: 'Connect Apple or Google calendar',
    free: 'Free.',
    today: 'today',
    allDay: 'all day',
    allDayLong: 'All day',
    cancelled: 'Cancelled',
    training: 'Training',
    imported: 'Imported',
    addOn: 'Add event on {{date}}',
    entries_one: '1 entry',
    entries_other: '{{count}} entries',
    fromSource: 'From "{{source}}" — it can only be changed there.',
    kind: {
      event: 'Private',
      household: 'Household',
      training: 'From your training plan',
      task: 'Task · {{list}}',
    },
    editor: {
      forWhom: 'For whom',
      forWhomHint:
        'Nobody selected means: for everyone. Sport counts in your balance only if you are included.',
      new: 'New event',
      edit: 'Edit event',
      title: 'Title',
      date: 'Day',
      endDate: 'Until (inclusive)',
      time: 'Time',
      duration: 'Duration (min)',
      location: 'Place',
      note: 'Note',
      activity: 'Activity',
      noActivity: 'None',
      activityMin: 'Minutes',
      activityWhy:
        'With an activity the event counts in your energy balance — as expected until it is over; not at all if cancelled.',
      shared: 'Share with household',
      save: 'Save',
      delete: 'Delete event',
      deleteWarning: 'The event will be deleted — shared ones for the others in the household too.',
    },
    settings: {
      heading: 'Connect calendars',
      importHeading: 'Show an Apple or Google calendar',
      importIntro:
        "Via your calendar's secret subscription link — no password. Kraftwerk only reads; changes still happen in Apple/Google.",
      events_one: '1 event',
      events_other: '{{count}} events',
      fetched: 'fetched {{date}}',
      failed: 'Last fetch failed: {{reason}}',
      remove: 'Remove',
      removeNamed: 'Remove {{name}}',
      name: 'Name',
      defaultName: 'Calendar',
      url: 'Subscription link (webcal:// or https://)',
      howTo: 'Where do I find the link?',
      howToApple:
        'Apple (iCloud): Calendar app on the Mac or icloud.com → share calendar → "Public calendar" → copy link.',
      howToGoogle:
        'Google: calendar.google.com → calendar settings → copy "Secret address in iCal format".',
      add: 'Add',
      adding: 'Fetching the calendar …',
      addFeed: 'Add calendar',
      addFailed: 'Not added: {{reason}}',
      exportHeading: 'Show Kraftwerk in Apple/Google',
      exportIntro:
        'A subscription address with your events and training. Apple/Google only display it (Apple refreshes about hourly, Google up to a day).',
      onlyOnce: 'The address is only shown now. Anyone who has it can see your events.',
      openApple: 'Subscribe in Apple Calendar',
      copy: 'Copy address',
      googleHint:
        'Google: calendar.google.com → "Other calendars" → "From URL" → paste the address.',
      active: 'A subscription address is set up.',
      password: 'Password to confirm',
      passwordWrong: "Wrong password — or the server can't be reached.",
      create: 'Create subscription address',
      renew: 'New address (the old one stops working)',
      revoke: 'End subscription',
    },
  },
  letter: {
    open: 'Read a school letter',
    heading: 'Read a school letter',
    privacy:
      'Photo and text stay on this device: it is read here in the browser, nothing is uploaded, and the photo is not saved. Only what you take over syncs like any other event.',
    firstLoad:
      'The first time, the app loads text recognition (about 3 MB) from your Kraftwerk server — after that it also works offline.',
    camera: 'Take a photo',
    pick: 'Choose a picture',
    typeIt: 'Type the text yourself',
    textLabel: 'Text of the letter',
    textHint: 'Type or paste it — the same rules apply as for a photo.',
    analyze: 'Analyse',
    reanalyze: 'Analyse again',
    back: 'Back',
    loading: 'Loading text recognition …',
    reading: 'Reading the letter …',
    progress: '{{percent}} %',
    cancel: 'Cancel',
    error: {
      ocr: 'Text recognition did not work. Try a sharper photo in good light — or type the text yourself.',
      offline:
        'Text recognition could not be loaded — offline? It comes once from your own server. You can also type the text yourself.',
      save: 'Saving did not work. Nothing is lost — please try again.',
    },
    recognized: 'Recognised text',
    recognizedHint: 'If something is wrong, correct it here and analyse again.',
    nothing: 'The text contains nothing that looks like an event, a deadline or an amount.',
    nothingWay: 'Check the text',
    newEvent: 'Add an event yourself',
    emptyText: 'No text could be recognised in the photo.',
    forWhom: 'Who is the letter for?',
    forAll: 'Everyone',
    suggestions: 'Suggestions',
    suggestionsHint: 'Uncertain ones are not preselected. Every one can be changed.',
    take: 'Take over',
    kindLabel: 'Type',
    kind: {
      event: 'Event',
      task: 'Task',
    },
    title: 'Title',
    date: 'Date',
    dueDate: 'Due',
    time: 'Time',
    endDate: 'until',
    noDate: 'No date found — please enter one.',
    noTitle: 'Title missing.',
    list: 'List',
    newList: 'New list “{{name}}”',
    newListName: 'School',
    for: 'For',
    source: 'From the letter: “{{line}}”',
    confidence: {
      high: 'recognised',
      medium: 'please check',
      low: 'uncertain',
    },
    doubt: {
      ocrFixed: 'Digits corrected from text recognition',
      monthGuessed: 'Only the day was given, the month was added',
      weekdayMismatch: 'Weekday and date do not match',
      weekdayOnly: 'Only a weekday — counted from today',
      relativeDay: '“Next week” — counted from today, not from the letter’s date',
      noDate: 'No date found',
      dateFromContext: 'Date taken from another sentence',
      titleGuessed: 'No keyword found — check the title',
      amountUnclear: 'Amount not clearly readable',
    },
    titles: {
      untitled: 'From the school letter',
      money: 'Give {{amount}}',
      moneyFor: 'Give {{amount}} – {{subject}}',
      transfer: 'Transfer {{amount}}',
      transferFor: 'Transfer {{amount}} – {{subject}}',
      moneyUnclear: 'Give money (check the amount)',
      bring: 'Bring: {{items}}',
      deadline: 'Deadline: {{subject}}',
      deadlineGeneric: 'Deadline from the school letter',
    },
    skipped: 'Not suggested',
    skip: {
      past: 'Already past',
      letterDate: 'Date of the letter',
    },
    apply_one: 'Take over {{count}} suggestion',
    apply_other: 'Take over {{count}} suggestions',
    applying: 'Saving …',
    incomplete: 'A selected suggestion is missing a title or date.',
    saved: 'Saved — events: {{events}}, tasks: {{tasks}}.',
    savedHint: 'They are now in the calendar or the list — the photo was not saved.',
    done: 'Done',
    another: 'Read another letter',
  },
  plan: {
    label: 'Plan',
    calendar: 'Calendar',
    tasks: 'Tasks',
    scope: {
      label: 'What to show',
      mine: 'Mine',
      ours: 'Ours',
      all: 'Everything',
      hintMine: 'Your own events, training, private lists — and shared items that concern you.',
      hintOurs: 'What your household shares: household events and shared lists.',
      hintAll: 'Everything from every space in one plan.',
    },
  },
  tasks: {
    reward: 'Pocket money for doing it (€)',
    rewardWhy:
      'Goes to a child with “money for done tasks” switched on when they tick it off. Empty = no money.',
    rewardInvalid: 'Please enter an amount up to {{max}}, such as 0.50.',
    rewardShort: '{{amount}} for doing it',
    stars: 'Stars for doing it',
    starsNone: 'None',
    starsShort_one: '{{count}} star',
    starsShort_other: '{{count}} stars',
    starsWhy: 'Goes to a child with “stars for done tasks” switched on when they tick it off.',
    fairness: 'Done in the household this week',
    someone: 'Someone',
    heading: 'Tasks',
    due: 'Due',
    nothingDue: 'Nothing due in the next seven days.',
    lists: 'Lists',
    newList: 'New list',
    noLists: 'No lists yet.',
    noListsInScope: 'No list in this selection — “Everything” shows them all.',
    kindLabel: 'Kind of list',
    kind: { tasks: 'Tasks', checklist: 'Checklist (template)' },
    kindWhy: {
      tasks: 'Tasks with a day, time, repetition and someone in charge.',
      checklist:
        'A template you tick off fresh each run — a packing list, say. Finished runs show in the history.',
    },
    shared: 'household',
    private: 'private',
    shareWithHousehold: 'Share with household',
    makePrivate: 'Make private again',
    cannotUnshare: "Can't make it private: others in the household have added things here.",
    listName: 'Name',
    create: 'Create',
    rename: 'Rename',
    listSettings: 'List',
    deleteList: 'Delete list',
    deleteWarning: 'The list and all its tasks will be deleted — shared ones for the others too.',
    emptyList: 'Nothing in this list yet.',
    addTask: 'Add task',
    addItem: 'Add item',
    addSub: 'Sub-item',
    editTask: 'Edit task',
    deleteTask: 'Delete task',
    finishRun: 'Finish run ({{done}}/{{total}})',
    runResult: '{{done}} of {{total}} done',
    showHistory_one: 'Show done (1)',
    showHistory_other: 'Show done ({{count}})',
    hideHistory: 'Hide done',
    noHistory: 'Nothing done yet.',
    completeNamed: 'Tick off {{title}}',
    reopenNamed: 'Reopen {{title}}',
    overdue: 'overdue',
    overdueSince: 'overdue since {{date}}',
    today: 'today',
    repeats: 'repeating',
    every: {
      day_one: 'daily',
      day_other: 'every {{count}} days',
      week_one: 'weekly',
      week_other: 'every {{count}} weeks',
      month_one: 'monthly',
      month_other: 'every {{count}} months',
    },
    title: 'What',
    dueDate: 'Day',
    dueTime: 'Time',
    timeWhy: 'With a time a notification comes — to whoever is in charge.',
    repeat: 'Repeat',
    everyLabel: 'Every',
    unitLabel: 'Unit',
    unit: { day: 'days', week: 'weeks', month: 'months' },
    assignee: 'In charge',
    nobody: 'Nobody in particular',
    assigneeWhy: 'Only whoever is in charge gets the reminder and sees it on "Today".',
    note: 'Note',
    save: 'Save',
    todayHeading: 'Tasks today',
    allTasks: 'All tasks',
  },
  rings: {
    heading: 'This week',
    summary: '{{closed}} of {{total}} rings closed',
    value: {
      training: '{{done}} of {{goal}} workouts',
      protein: '{{done}} of {{goal}} days at protein target',
      supplements: '{{done}} of {{goal}} supplements taken',
    },
    noGoal: {
      training: 'No training days planned',
      protein: 'No protein target set',
      supplements: 'Nothing due yet',
    },
    setup: {
      training: 'Set training days',
      protein: 'Set a protein target',
      supplements: 'Set up supplements',
    },
    allClosed: 'All rings closed — strong week.',
    newBest: 'New best!',
    newBestValue: 'Estimated max ≈ {{kg}} kg (+{{gain}} kg).',
    records_one: '1 best day',
    records_other: '{{count}} best days',
    settingsHeading: 'Achievements',
    settingsIntro:
      'Training bests, weekly rings, streaks and badges — all computed from your entries, nothing made up.',
    settingsIntroTeen:
      'Workouts, streaks and badges — what you do is celebrated, never your body. Nobody in the household sees them.',
    settingsOn: 'Show achievements: on',
    settingsOff: 'Show achievements: off',
  },
  streaks: {
    heading: 'Streaks',
    supplements: 'Supplements complete',
    journal: 'Journal',
    trainingWeeks: 'Training weeks done',
    days_one: 'day',
    days_other: 'days',
    weeks_one: 'week',
    weeks_other: 'weeks',
    best: 'record {{count}}',
    jokerUsed_one: '1 joker used',
    jokerUsed_other: '{{count}} jokers used',
    jokerWhy_one:
      'One joker per month saves a daily streak when a day slips. Cancelled workouts never break a weekly streak.',
    jokerWhy_other:
      '{{count}} jokers per month save a daily streak when a day slips. Cancelled workouts never break a weekly streak.',
  },
  badges: {
    heading_one: 'Badges · 1 earned',
    heading_other: 'Badges · {{count}} earned',
    none: 'None yet — the first one comes with your first workout.',
    noneYet: 'None yet — how far it is to the next one is shown below.',
    progress: '{{done}} of {{goal}}',
    sessions_one: 'First workout',
    sessions_other: '{{level}} workouts',
    volume_one: '{{level}} kg in one workout',
    volume_other: '{{level}} kg in one workout',
    records_one: 'First personal best',
    records_other: '{{level}} personal-best days',
    supplementStreak_one: '{{level}} day of supplements in a row',
    supplementStreak_other: '{{level}} days of supplements in a row',
    trainingWeeks_one: '{{level}} training week in a row',
    trainingWeeks_other: '{{level}} training weeks in a row',
    recipes_one: '{{level}} dish eaten',
    recipes_other: '{{level}} different dishes eaten',
    journal_one: '{{level}} journal day',
    journal_other: '{{level}} journal days',
    foodDays_one: '{{level}} day of logged food',
    foodDays_other: '{{level}} days of logged food',
    waterDays_one: '{{level}} day water goal reached',
    waterDays_other: '{{level}} days water goal reached',
  },
  householdGoal: {
    heading: 'Together this week',
    alone: 'Nobody else in the household shares their training numbers yet.',
    together: 'Together {{done}} of {{planned}} workouts',
    noPlan: 'Together {{done}} workouts',
    member: '{{done}} of {{planned}}',
    me: 'You',
    settingsIntro:
      "Share this week's workouts (only planned and done, never details) with your household — for a joint weekly goal. Only those who share can see.",
    settingsOn: 'Share with household: on',
    settingsOff: 'Share with household: off',
  },
  e2e: {
    heading: 'Encryption',
    intro:
      'End-to-end: journal, photos and costs are encrypted on the device before they leave it. The server only stores unreadable data.',
    introHint:
      'You unlock with Face ID (your passkey). You also get a recovery code, once — without passkey and code nobody can restore your thoughts, not even us.',
    setup: 'Set up encryption',
    working: 'One moment …',
    codeIntro: 'Your recovery code — it is shown only this once:',
    codeLabel: 'Recovery code',
    copy: 'Copy code',
    copied: 'Copied',
    codeWarning:
      'Put it in your password manager now. You need it to unlock a device without your passkey.',
    codeSaved: 'I have stored the code safely.',
    finish: 'Done — encrypt now',
    ready:
      'Set up. Journal, photos, costs, shopping, lists, bucket list and notes about children only reach the server encrypted.',
    readyHint: 'On another device you unlock with Face ID or the recovery code.',
    householdWaiting:
      'Shared household data (costs, shopping, lists …) gets encrypted once every adult there has set up encryption. Until then it stays readable for everyone.',
    addPasskey: 'Add Face ID on this device',
    addPasskeyHint:
      'This device was unlocked with the code. With Face ID, next time works without it.',
    passkeyAdded: 'Face ID is set up for this device.',
    locked:
      'Your account is encrypted, this device is not unlocked yet. Until then new thoughts stay on the device.',
    unlockFaceId: 'Unlock with Face ID',
    codeInput: 'Or recovery code',
    unlockCode: 'Unlock with code',
    photoLocked: 'Encrypted — unlock this device in Settings.',
    sealedHeading_one: '1 entry is encrypted',
    sealedHeading_other: '{{count}} entries are encrypted',
    waitingParent:
      'They open as soon as a parent’s device has opened the app once. Until then, your changes to them stay on this device.',
    waitingHousehold:
      'Shared household entries. They open as soon as another device in the household has opened the app once.',
    error: {
      cancelled: 'Cancelled.',
      noPrf: 'This passkey cannot provide a key. A passkey from iCloud Keychain works.',
      otherAccount: 'That was the passkey of another account.',
      notVerified: 'Face ID was not confirmed.',
      unknownPasskey:
        'This passkey is not set up for encryption yet — unlock with the code, then add Face ID.',
      wrongCode: 'The code does not match.',
      exists:
        'Encryption was just set up on another device. Unlock this device with Face ID or the code.',
      offline: 'No connection to the server — please try again later.',
    },
  },
  serverView: {
    heading: 'What the server sees',
    tileIntro:
      'Kraftwerk encrypts end to end — with exceptions. Every one of them is listed here, with the reason.',
    open: 'See everything',
    back: 'Back',
    intro:
      'Encryption happens on your device before anything leaves it. What the server needs for notifications, bookings and the rules for children stays readable to it. This page lists all of it.',
    status: {
      heading: 'Your account',
      off: 'Encryption is not set up for your account. Until it is, the server can read everything — including what is listed as encrypted below.',
      offChild:
        'Your account is not encrypted yet. That happens by itself as soon as a parent’s device has opened the app once. Until then the server can read everything.',
      locked:
        'Your account is encrypted, but this device is not unlocked yet. New changes wait here until it is — no plain text leaves it.',
      ready: 'Your account is encrypted and this device is unlocked.',
      household:
        'Shared household data is only encrypted once every adult there has set up encryption. Until then the server can read it.',
      setup: 'Go to encryption',
    },
    sealed: {
      heading: 'Encrypted',
      intro:
        'Only your device can open this — shared items also the devices in your household. The server stores it but cannot read it.',
      photos: 'Journal and recipe pictures',
      tables: {
        journal_notes: 'Thoughts: text, title, mood, tags and the linked event',
        journal_entries: 'Journal day: sleep, energy, mood, soreness, note and measurements',
        journal_photos: 'Photo captions',
        recipe_cook_notes: 'Cooking notes and ratings',
        expense_categories: 'Cost categories: name and budget',
        recurring_expenses: 'Recurring costs: title and amount',
        expenses: 'Expenses: amount, category and note',
        savings_goals: 'Savings goals for costs: name and target',
        savings_deposits: 'Deposits to savings goals: amount',
        task_lists: 'Names of task lists',
        events: 'Your own events: the logged activity',
        household_events: 'Shared events: the logged activity',
        shopping_items: 'Shopping list: name, amount, unit, aisle and note',
        bucket_items: 'Bucket list: title and note',
        member_notes: 'Notes about children',
        exercises: 'Exercises: name, muscles, equipment, sets, rest, videos, cues and notes',
        plan_exercises: 'Exercises in a plan: order, sets, reps, rest and note',
        shops: 'Shops: name and aisle order',
        event_marks: 'Marks on subscribed events: activity, duration and “cancelled”',
        profiles:
          'Body data in your profile: height, year of birth, formula, everyday activity, goal, carb and fat targets',
        places: 'Household places: name, centre and radius',
        location_checkins:
          'Location check-ins: position, accuracy, place name, kind and the notification to the household',
        record_entries: 'Records: what kind of entry, everything in it and the link to planning',
        household_meals:
          'Household meal plan and wishes: dish with ingredients and nutrition, note and who cooks',
        household_meal_eaters:
          'Who eats along: portion, what is bought extra, own variant and the “vegetarian” hint',
        meal_deviations:
          'Your changes to the household plan: not eating, portion, extras, variant and standing rules',
        food_log:
          'Food log: what you ate, dish or food, amount, time, calories, carbs, fat, other nutrients, note and “estimated”',
      },
    },
    readable: {
      heading: 'Readable — and why',
      intro:
        'The server reads this because it acts on it while the app is closed. What it does not need is encrypted.',
      also: 'Also for: {{reasons}}',
      reasons: {
        reminders: {
          title: 'Notifications and reminders',
          why: 'They arrive while the app is closed. The server has to know when — and what they say.',
        },
        review: {
          title: 'Weekly review',
          why: 'The server works it out on Sundays and sends it as a notification.',
        },
        shortcuts: {
          title: 'Shortcuts',
          why: 'Siri and Apple Health log without the app — the server checks and writes it.',
        },
        bookings: {
          title: 'Bookings by the server',
          why: 'The server books pocket money, money and stars for finished tasks itself — so nothing is booked twice or not at all.',
        },
        childRules: {
          title: 'Rules for children',
          why: 'The server checks what a child may see and change. A limit only inside the app would be none.',
        },
        household: {
          title: 'Household',
          why: 'Who belongs decides who sees shared items. Others only see your weekly workout count if you share it.',
        },
        calendarExport: {
          title: 'Calendar subscription',
          why: 'Apple and Google Calendar fetch your events straight from the server, without the app.',
        },
        calendarImport: {
          title: 'Imported calendars',
          why: 'The server fetches subscribed calendars from their provider.',
        },
        sharing: {
          title: 'Giving recipes away',
          why: 'When you give a recipe to someone in your household, the server copies it along with its foods.',
        },
        templates: {
          title: 'New templates',
          why: 'The server adds new exercises and plans from the app to your account. It uses the template’s identifier to tell what is already there.',
        },
        notYet: {
          title: 'Not encrypted yet',
          why: 'The server does not need this — it just has not been switched over yet.',
        },
      },
      tables: {
        profiles:
          'Settings: time zone, language, times, modules, calorie and protein targets and your weight',
        supplements: 'Supplements with dose and time',
        supplement_intakes: '“Taken” entries',
        reminders: 'Reminders the server creates itself',
        exercises: 'Exercises: only which template they come from',
        training_plans: 'Training plans',
        plan_days: 'Plan days and their names',
        workout_sessions: 'Workouts: start and end',
        workout_sets: 'Sets: weight, reps, time, distance and note',
        foods: 'Foods with nutrition values',
        recipes: 'Recipes with ingredients and steps',
        recipe_ingredients: 'Recipe ingredients',
        meal_plan_entries: 'Meal plan',
        food_log: 'Food log: only day, meal and protein — what you ate is encrypted',
        meal_skips: 'Skipped meals',
        water_log: 'Water you drank',
        prep_checks: 'Ticked-off preparations',
        training_slots: 'Training days: weekday and time',
        training_days: 'Moved and cancelled training sessions',
        events: 'Your own events: title, time, place and note',
        household_events: 'Shared events: title, time, place and note',
        feed_events: 'Events from subscribed calendars',
        journal_entries: 'Journal day: weight and steps',
        households: 'Households and their name',
        household_members:
          'Members: display name, role, year of birth, plus level, pocket money and star rules for children',
        pantry_items: 'Pantry',
        tasks: 'Tasks: title, note, due date, assignee and reward',
        task_completions: 'Finished tasks',
        pocket_goals: 'Children’s savings goals',
        pocket_money: 'Pocket money bookings',
        star_entries: 'Stars',
        places: 'Places: only the household and who created them',
        location_checkins:
          'Location check-ins: only who, in which household and when — the place itself is encrypted',
        record_entries:
          'Records: only who keeps them, about whom, in which household, who may see them and when — the content is encrypted',
        household_meals:
          'Household meal plan: only day, meal, plan or wish and who created it — the dish is encrypted',
        household_meal_eaters:
          'Who eats along: only which entry and which person — the portion is encrypted',
        meal_deviations: 'Your changes: only which entry they point to — the content is encrypted',
      },
    },
    always: {
      heading: 'What the server always sees',
      intro:
        'Even encrypted items sit in a readable envelope — otherwise the server could neither sync nor keep accounts apart.',
      envelope:
        'For every entry: its ID, account, household, when it was created, changed or deleted, its date (for thoughts also the time), links to other entries, done marks — and for costs, who paid and how often.',
      photos: 'For every picture: file size, dimensions and when it was uploaded.',
      storage: 'How much storage your account uses.',
      photoEstimate:
        'A photo you explicitly send for a calorie estimate: the server keeps it in memory only until it is estimated (in the background, usually a few minutes) and never stores it — after a restart it is gone. Depending on the setup it estimates itself or passes it on to Anthropic — which one, the notice before sending tells you. It sees the job with day, meal and status; it keeps the result until you take it over, at most 24 hours, encrypted as soon as your account is encrypted.',
      coach:
        'The summary you explicitly send to the AI coach (numbers, training days, exercise names — never thoughts, notes or weight): the server sees it for the duration of the reply and does not store it. Depending on the setup its own model or Anthropic answers — which one, the notice before sending tells you.',
      serverOnly: {
        account: 'Account: email address, name and quota — the password only as a hash',
        sessions: 'Sign-ins: IP address, browser and when last used',
        passkeys: 'Passkeys: name and public key',
        keys: 'Encryption keys — only wrapped, unusable without a passkey or the code',
        tokens: 'Keys for shortcuts and the calendar subscription — only as a hash',
        push: 'Notification subscriptions: address at the push service, browser and whether delivery works',
        restTimer: 'Rest timer end during training, with the next set',
        calendarFeeds: 'Addresses of subscribed calendars — the server fetches them, after all',
        codes: 'Invite codes and sign-in codes for children — only as a hash',
        estimateJobs:
          'Photo estimates: day, meal, status and reason, at most 24 hours — the result encrypted, the photo never',
      },
    },
    backups: {
      heading: 'Backups',
      body: 'The nightly database backup is stored encrypted; the key to open it is not on the server. Pictures are backed up as copies — encrypted exactly as they were uploaded.',
    },
  },
  storage: {
    heading: 'Storage',
    used: '{{used}} of {{quota}} used',
    split: 'Entries {{data}} · Images {{files}}',
    why: 'Everything together: entries, journal photos and dish images.',
    full: 'Storage full — new photos stay on this device and upload once there is room again. Entries keep being saved.',
    fullRecipe: 'Storage full — the image was not saved.',
    waiting_one: '1 photo is waiting to upload.',
    waiting_other: '{{count}} photos are waiting to upload.',
  },
  children: {
    heading: 'Kids',
    badge: 'Kid',
    intro:
      'Kids need no email and no password. You plan for them, write notes and, if you like, sign in their own device.',
    age: '{{age}} years',
    add: 'Add a kid',
    name: 'Name',
    birthYear: 'Year of birth',
    addSubmit: 'Add',
    offline: 'This only works while connected to the server.',
    gone: 'This kid is no longer in the household.',
    device: 'Own device',
    deviceHint:
      'On {{name}}’s phone open Kraftwerk, tap “For kids: sign in with a code” and enter the code.',
    makeCode: 'Create code',
    newCode: 'Create a new code',
    codeExpires: 'Valid until {{time}}, and only once.',
    notes: 'Notes',
    noNotes: 'No notes yet.',
    noteBody: 'Note about {{name}} …',
    whoSees: 'Who can see it?',
    visibility: {
      me: 'Only me',
      adults: 'Adults in the household',
      child: '{{name}} too',
    },
    saveNote: 'Save note',
    deleteNote: 'Delete',
    remove: 'Remove {{name}} from the household',
    removeWarning:
      '{{name}} will be signed out and sees nothing from this household any more. Events and notes stay with you.',
    removeConfirm: 'Remove',
    forWhomHint:
      'Events and tasks for a kid are made in Planning: “For whom” on events, “Assigned to” on tasks.',
    forMe: 'For you',
    forMeEmpty: 'No message for you right now.',
    level: {
      heading: 'What can they do on their own?',
      readOnly: 'You can look — your parents can let you add things.',
      small: 'Little',
      school: 'School kid',
      teen: 'Teen',
      smallHint:
        '{{name}} sees events and tasks and ticks off their own tasks. You do the planning and the shopping list.',
      schoolHint: '{{name}} adds their own events and tasks and writes on the shopping list.',
      teenHint:
        '{{name}} plans on their own, writes on the shopping list and takes on household tasks.',
    },
    nutrition: {
      heading: 'Nutrition',
      toggle: 'Nutrition with own targets',
      onHint:
        '{{name}} logs food and sets their own calorie and protein targets. Weight, height and supplements stay hidden.',
      offHint:
        'Off: {{name}} does not see nutrition. Turn it on and {{name}} logs food and sets their own targets — without weight, height or supplements.',
      onlyTeen:
        'Nutrition with own targets is available from the “Teen” level. Entries by {{name}} are kept but hidden.',
    },
    training: {
      heading: 'Training',
      toggle: 'Training with plans and sets',
      onHint:
        '{{name}} plans training, logs sets with weights and sees their own progress, plus achievements for consistency. Body weight and energy stay hidden.',
      offHint:
        'Off: {{name}} does not see training. Turn it on and {{name}} plans sessions and training days with reminders — without body weight or energy.',
      onlyTeen:
        'Training is available from the “Teen” level. Sessions by {{name}} are kept but hidden.',
    },
    water: {
      heading: 'Water target',
      save: 'Apply',
      loading: 'Loading …',
      default: 'Default: {{ml}} ml a day. {{name}} can change the target too.',
      current: 'Set: {{ml}} ml a day. {{name}} can change the target too.',
      saved: 'Saved — {{name}} sees the new target after the next sync.',
      invalid: 'Please enter a whole number between 250 and 10000 ml.',
      needsNutrition:
        'Water is part of nutrition. Turn on nutrition first, then set the target for {{name}} here.',
    },
  },
  family: {
    heading: 'Family display',
    open: 'Open family display',
    intro:
      'For a tablet in the kitchen: clock, household events, who is up today, the shopping list and the children’s stars — large and without the tab bar. Only shared things everyone may see are shown; never your own items, body data, costs or pocket money.',
    exit: 'Exit',
    rewardsToggle: 'Ticking off on the tablet counts for rewards',
    rewardsOn:
      'On by default: ticking off a child’s task on the family display ticks it off for the child — they get the stars and, if switched on, the money. Applies to this device.',
    rewardsOff:
      'Off: tasks ticked off on the tablet are done but bring neither stars nor money. Only what the child ticks off themselves is rewarded. Applies to this device.',
    noRewardsHere: 'Ticked off here, there are no stars and no money.',
    today: 'Today',
    tomorrow: 'Tomorrow',
    noEventsToday: 'No events today.',
    noEventsTomorrow: 'No events tomorrow.',
    allDay: 'All day',
    cancelled: 'Cancelled',
    forWhom: 'for {{names}}',
    tasks: 'Who is up',
    noTasks: 'Nothing open today.',
    everyone: 'For everyone',
    overdue: 'Overdue',
    dueToday: 'Today',
    taskStars_one: '{{count}} star',
    taskStars_other: '{{count}} stars',
    completeNamed: '“{{title}}” done',
    completed: '“{{title}}” done.',
    undo: 'Undo',
    shopping: 'Shopping list',
    shoppingEmpty: 'The shopping list is empty.',
    shoppingOpen_one: '{{count}} item open',
    shoppingOpen_other: '{{count}} items open',
    shoppingMore_one: 'and {{count}} more',
    shoppingMore_other: 'and {{count}} more',
    stars: 'Stars',
    starGoal: '{{name}}: stars towards {{goal}}',
    noStarGoal: 'No reward set yet.',
    noHousehold: 'No household on this device yet — sync once while online.',
    noWakeLock:
      'This device does not keep the screen on by itself — turn off auto-lock in the tablet’s settings.',
  },
  record: {
    heading: 'Records',
    ownHeading: 'My records',
    ownIntro:
      'Doctors, vaccinations, allergies, insurance, IDs — for you alone, not even for the household. Encrypted on your device.',
    childIntro:
      'What the adults in the household should know about {{name}}. Encrypted; {{name}} only sees it if you share it.',
    sharedHeading: 'My records',
    sharedIntro: 'What your parents have shared with you.',
    empty: 'No entries yet — everywhere: Not specified.',
    emptyShared: 'Not specified — your parents have not shared anything.',
    add: 'Add entry',
    pickKind: 'What would you like to add?',
    formLabel: 'Add {{kind}}',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    deleteWarning: 'Delete this entry? Tasks or events made from it stay.',
    deleteConfirm: 'Yes, delete',
    invalid: 'Please fill in the name and give dates as a day.',
    noValue: 'Not specified',
    whoSees: 'Who sees this?',
    visibility: {
      adults: 'Adults only',
      child: '{{name}} too',
    },
    medicationHint:
      'Only what you enter yourselves — for example as the doctor prescribed. Kraftwerk does not recommend any dosage.',
    sizeHint: 'Clothes and shoes. Height and weight do not belong here.',
    heldOwn_one:
      'One entry is waiting on your device: records only leave encrypted. Set up encryption.',
    heldOwn_other:
      '{{count}} entries are waiting on your device: records only leave encrypted. Set up encryption.',
    heldHousehold_one:
      'One entry is waiting on this device until the household is encrypted — for that, every adult sets up encryption under Settings → Privacy.',
    heldHousehold_other:
      '{{count}} entries are waiting on this device until the household is encrypted — for that, every adult sets up encryption under Settings → Privacy.',
    toEncryption: 'Go to encryption',
    due: {
      overdue: 'due since {{date}}',
      soon: 'due on {{date}}',
      later: 'due on {{date}}',
    },
    plan: 'Add to planning',
    planAs: 'As',
    planKinds: {
      task: 'Task',
      event: 'Event',
    },
    planTitle: '{{kind}}: deadline',
    planTitleFor: '{{kind}}: deadline for {{name}}',
    planTitleLabel: 'Title',
    planServerHint:
      'The server reads titles of tasks and events for notifications and the calendar feed — better leave out health details.',
    planDate: 'On',
    planTime: 'Time',
    planTaskHint: 'A notification comes at that time, like for any task.',
    planTaskNoTime: 'Without a time it only sits in the list — no notification then.',
    planEventHint: 'Shows in the calendar. No notification comes for it — choose a task for that.',
    planSubmit: 'Add',
    planned: {
      task: 'Planned as a task (deadline {{date}}).',
      event: 'In the calendar as an event (deadline {{date}}).',
      household_event: 'In the calendar as a shared event (deadline {{date}}).',
    },
    planOutdated: 'The deadline changed — the planned entry has not.',
    taskList: 'Deadlines',
    kinds: {
      doctor: 'Doctor',
      vaccination: 'Vaccination',
      checkup: 'Check-up',
      allergy: 'Allergy / intolerance',
      medication: 'Medication',
      size: 'Clothing size',
      school: 'School / daycare',
      emergency: 'Emergency contact',
      insurance: 'Insurance card',
      document: 'ID / document',
    },
    fields: {
      doctor: {
        name: 'Name',
        specialty: 'Specialty',
        phone: 'Phone',
        address: 'Address',
      },
      vaccination: {
        vaccine: 'Vaccination',
        date: 'Given on',
        nextDue: 'Next due',
        note: 'Note',
      },
      checkup: {
        name: 'Check-up (e.g. U7)',
        date: 'On',
        nextDue: 'Next due',
        note: 'Note',
      },
      allergy: {
        substance: 'To what',
        reaction: 'Reaction',
        note: 'Note',
      },
      medication: {
        name: 'Name',
        note: 'Note',
      },
      size: {
        item: 'What (e.g. shoes)',
        size: 'Size',
        note: 'Note',
      },
      school: {
        name: 'School / daycare',
        group: 'Class / group',
        teacher: 'Teacher',
        phone: 'Phone',
        address: 'Address',
      },
      emergency: {
        name: 'Name',
        relation: 'Relation',
        phone: 'Phone',
      },
      insurance: {
        insurer: 'Insurer',
        number: 'Member number',
        phone: 'Phone',
      },
      document: {
        name: 'Document (e.g. ID card)',
        number: 'Number',
        expiresOn: 'Valid until',
      },
    },
  },
  location: {
    heading: 'Location',
    intro:
      'Sharing only works while the app is open — a web app gets no location in the background. Place, name and accuracy are end-to-end encrypted; the server only sees that someone shared.',
    held_one:
      '1 change is waiting for the household key. It goes out once all adults have set up encryption — never unencrypted.',
    held_other:
      '{{count}} changes are waiting for the household key. They go out once all adults have set up encryption — never unencrypted.',
    childOff: 'Your parents can turn on location sharing for you.',
    arrived: 'I’ve arrived',
    here: 'I’m here',
    live: 'Share location while the app is open',
    locating: 'Finding location …',
    confirm: {
      arrived: 'Arrived — share like this?',
      here: 'Here — share like this?',
      live: 'Share location while the app is open?',
    },
    placeChoice: 'Place',
    noPlace: 'No place name',
    outside: 'Outside all places — {{place}} is {{distance}} away.',
    duration: 'How long',
    minutes_one: '{{count}} minute',
    minutes_other: '{{count}} minutes',
    liveForeground:
      'Updates every {{minutes}} minutes, only while the app is in front. In the background or closed, sharing ends — it does not keep running there.',
    liveOn: 'You are sharing your location until {{time}}.',
    liveStop: 'Stop sharing',
    send: 'Share',
    cancel: 'Cancel',
    savedHeld: 'Saved. It goes out as soon as the household key is there.',
    sent: 'Shared. The other adults get a notification.',
    error: {
      unsupported: 'This device cannot determine its location.',
      denied:
        'Location access is not allowed. Allow it in the phone’s settings (Privacy → Location Services) and tap again.',
      timeout: 'No location within 20 seconds. Try again outside or by a window.',
      unavailable: 'The location cannot be determined right now. Try again in a moment.',
    },
    accuracy: 'Accurate to about {{distance}}',
    accuracyCoarse: 'Imprecise: about {{distance}} — more the area than the place',
    accuracyUnknown: 'Accuracy: not stated',
    whoIsWhere: 'Who is where',
    mine: 'Your location',
    none: 'Nothing shared yet.',
    me: 'Me',
    someone: 'Someone',
    kind: {
      arrived: 'arrived',
      here: 'here',
      live: 'sharing',
    },
    unnamed: 'unnamed place',
    unnamedNear: 'unnamed place, {{distance}} from {{place}}',
    when: '{{day}}, {{time}}',
    liveBadge: 'sharing now',
    openMaps: 'Open in Maps',
    withdraw: 'Withdraw',
    history_one: 'History: 1 more',
    history_other: 'History: {{count}} more',
    retention:
      'Kept for {{days}} days. After that only each person’s last location stays, everything older is deleted — on the server too.',
    places: 'Places',
    noPlaces: 'No places yet. If a shared location lies within one, it is suggested.',
    radius: 'Radius {{distance}}',
    delete: 'Delete',
    deletePlace: 'Delete {{place}}',
    addPlace: 'Add place',
    placeName: 'Name, e.g. School or Grandma',
    placeRadius: 'Radius',
    placeHint: 'The place you are at right now is saved.',
    savePlace: 'Save here as a place',
    childToggle: 'Share location',
    childOnHint: '{{name}} can share where they are. Only {{name}} and the adults can see it.',
    childOffHint: 'Off: {{name}} does not share a location.',
  },
  stars: {
    heading: 'Stars',
    count_one: '{{count}} star',
    count_other: '{{count}} stars',
    toggle: 'Stars for done tasks',
    off: 'Off by default. Good for children who can’t do much with money yet: when on, {{name}} gets the stars you pick on a task as soon as it’s ticked off — once per task and day, never retroactively.',
    on: 'On since {{date}}: pick the stars on the task (Plan → Tasks).',
    noGoal: 'No reward set yet.',
    setGoal: 'Set a reward',
    changeGoal: 'Change reward',
    goalHeading: 'Reward',
    goalName: 'What they get',
    goalNamePlaceholder: 'Ice cream',
    goalStars: 'Stars',
    goalHint: 'For example “ice cream” for 10 stars. Both fields empty = no reward.',
    goalInvalid: 'Please fill in both: what they get and how many stars (1 to {{max}}).',
    saveGoal: 'Save reward',
    missing_one: '{{count}} more star until {{name}}',
    missing_other: '{{count}} more stars until {{name}}',
    reached: 'Done — enough stars for {{name}}!',
    redeemGoal_one: 'Redeem {{name}} ({{count}} star)',
    redeemGoal_other: 'Redeem {{name}} ({{count}} stars)',
    bonus: 'Give extra stars',
    redeem: 'Redeem stars',
    amount: 'Stars',
    note: 'Reason',
    amountInvalid: 'Please enter a whole number from 1 to {{max}}.',
    book: 'Book',
    removeNamed: 'Take back “{{what}}”',
    kind: {
      task: 'Task done',
      bonus: 'Extra',
      redeem: 'Redeemed',
    },
  },
  pocket: {
    heading: 'Pocket money',
    balance: 'Pocket money now',
    noRule: 'No regular pocket money yet.',
    ruleWeekly: '{{amount}} every {{day}}',
    ruleMonthly: '{{amount}} every month on day {{day}}.',
    weekday: {
      '1': 'Monday',
      '2': 'Tuesday',
      '3': 'Wednesday',
      '4': 'Thursday',
      '5': 'Friday',
      '6': 'Saturday',
      '7': 'Sunday',
    },
    kind: {
      allowance: 'Pocket money',
      gift: 'Got extra',
      expense: 'Spent',
      save: 'Put aside',
      withdraw: 'Taken back',
      reward: 'For a task',
    },
    rewards: {
      toggle: 'Money for done tasks',
      off: 'Off by default. When on, Kraftwerk books the amount you enter on a task as soon as {{name}} ticks it off — once per task and day, never retroactively.',
      on: 'On since {{date}}: enter the amount on the task (Plan → Tasks). Reopened means the booking is dropped.',
    },
    goals: {
      heading: 'Savings goals',
      empty: 'No savings goal yet — say, for a bike.',
      add: 'Add savings goal',
      create: 'Add',
      name: 'What for',
      target: 'Goal (€)',
      split: 'Free: {{free}} · saved: {{saved}}',
      progress: '{{saved}} of {{target}}',
      missing: '{{amount}} to go',
      reached: 'reached',
      gone: 'Savings goal deleted',
      removeNamed: 'Delete savings goal “{{name}}”',
      action: { save: 'Put aside', withdraw: 'Take back', gift: 'Add money' },
      tooMuchSave: 'Only {{amount}} is free.',
      tooMuchWithdraw: 'Only {{amount}} is saved.',
    },
    empty: 'Nothing yet.',
    removeNamed: 'Remove “{{what}}”',
    bought: 'I bought',
    addHeading: 'Add',
    amount: 'Amount (€)',
    note: 'What for',
    save: 'Add',
    setRule: 'Set up regular pocket money',
    changeRule: 'Change pocket money',
    ruleHeading: 'Regular pocket money',
    interval: { weekly: 'Weekly', monthly: 'Monthly' },
    day: 'Which day',
    monthDay: 'day {{day}}',
    ruleHint: 'Booked from today, never backdated — automatically on every due day.',
    saveRule: 'Save',
    endRule: 'Stop',
    cancel: 'Cancel',
  },
  costs: {
    heading: 'Costs',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    vsPrevious: 'Previous month: {{amount}}',
    noPrevious: 'Nothing recorded the month before.',
    add: 'Add an expense',
    edit: 'Edit expense',
    amount: 'Amount (€)',
    date: 'Date',
    note: 'What for (optional)',
    invalidAmount: 'Please enter an amount like 12.50.',
    categoryLabel: 'Category',
    paidBy: 'Paid by',
    privateToggle: 'Only for me',
    private: 'private',
    addSubmit: 'Add',
    save: 'Save',
    delete: 'Delete',
    skip: 'Skip',
    byCategory: 'By category',
    byPerson: 'Who spent what',
    someone: 'Someone',
    unknownPayer: 'Not stated',
    list: 'Expenses',
    empty: 'Nothing recorded this month yet.',
    budgetWarn: 'Over 80 % of the budget.',
    budgetOver: 'Budget exceeded.',
    setBudget: 'Set a budget for …',
    budgetFor: 'Monthly budget for {{category}}',
    budgetPlaceholder: 'empty = no budget',
    recurring: 'Recurring',
    recurringEmpty: 'Nothing yet — rent, club fees, gym.',
    recurringTitle: 'What',
    day: 'On day',
    addRecurring: 'Add a recurring expense',
    deleteRecurring: 'Remove “{{title}}”',
    interval: {
      monthly: 'monthly on day {{day}}',
      yearly: 'yearly on day {{day}}',
    },
    intervalName: { monthly: 'Monthly', yearly: 'Yearly' },
    goals: 'Savings goals',
    goalsEmpty: 'No savings goal yet.',
    goalName: 'What for',
    goalTarget: 'Target (€)',
    addGoal: 'Add a savings goal',
    goalProgress: '{{percent}} % reached',
    depositOpen: 'Add money',
    deposit: 'Add',
    depositAmount: 'Amount for {{name}}',
    shoppingQuestion: 'What did the shopping cost?',
    shoppingHint: 'Recorded as groceries, paid by you — change it under Costs.',
    category: {
      food: 'Groceries',
      home: 'Home',
      kids: 'Kids',
      sport: 'Sport',
      leisure: 'Leisure',
      mobility: 'Transport',
      health: 'Health',
      other: 'Other',
    },
  },
  nutrients: {
    name: {
      fiber: 'Fibre',
      sugar: 'Sugar',
      salt: 'Salt',
      vitaminD: 'Vitamin D',
      vitaminB12: 'Vitamin B12',
      folate: 'Folate',
      vitaminC: 'Vitamin C',
      iron: 'Iron',
      calcium: 'Calcium',
      magnesium: 'Magnesium',
      zinc: 'Zinc',
      iodine: 'Iodine',
    },
    withUnit: '{{name}} ({{unit}})',
    per100g: 'per 100 g',
    perPortion: 'per portion',
    microsHeading: 'Vitamins and minerals ({{basis}}, if on the package)',
    dayHeading: 'Fibre, sugar, salt',
    range: { day: 'This day', week: '7 days' },
    rangeLabel: 'Period',
    noEntriesDay: 'Nothing logged on this day.',
    microsWeekHeading: 'Vitamins and minerals',
    noEntries: 'Nothing logged in the last seven days.',
    averageOver_one: 'Average of the one day with entries in the last seven.',
    averageOver_other: 'Average over {{count}} days with entries in the last seven.',
    nrvNote:
      'Compared with the EU reference values from the package (NRV) — a yardstick, not your personal need.',
    unknown: 'unknown',
    symbolLabel: '{{name}}: {{amount}} of {{reference}}',
    detail: '{{amount}} {{unit}} of {{reference}} {{unit}}.',
    detailAverage: 'On average {{amount}} {{unit}} a day, compared with {{reference}} {{unit}}.',
    detailUnknown: 'No entry has a value for {{name}} — you can add it on the food.',
    kind: {
      min: 'Guide value (at least) from the',
      max: 'Reference intake (upper limit) from the',
      nrv: 'Nutrient reference value from the',
    },
    source: {
      dge: 'German Nutrition Society (DGE).',
      lmivReference: 'EU food information regulation (as on packages).',
      lmivNrv: 'EU food information regulation (as on packages).',
    },
    coverage:
      'Only {{known}} of {{entries}} entries have a value — the total is at least this high.',
    sugarNote:
      'The package counts all sugar, including from fruit and milk. For added sugar, WHO and DGE recommend less than 10% of energy.',
  },
  shops: {
    all: 'All',
    filter: 'Filter by shop',
    manage: 'Shops',
    intro:
      'One list for all shops: an item can have a shop, otherwise it shows everywhere. The app remembers where you last bought something.',
    name: 'Shop name',
    add: 'Add shop',
    remove: 'Remove {{name}}',
    shelves: 'Shelves',
    shelvesIntro: 'The way you walk through this shop — the list sorts itself accordingly.',
    shelfUp: 'Move {{name}} forward',
    shelfDown: 'Move {{name}} back',
    shop: 'Shop',
    anywhere: 'anywhere',
    trips: 'Shopping days',
    planTrip: 'Plan shopping',
    noTrips: 'None planned. With a shopping day, “From the week plan” covers until the next trip.',
    removeTrip: 'Remove shopping on {{day}}',
    timeOptional: 'Time (optional)',
    save: 'Save',
    tripTitle: 'Shopping',
    tripTitleShop: 'Shopping · {{shop}}',
    untilNextTrip: 'Until the next shopping trip on {{day}}.',
    todayHeading: 'Shopping today',
    todayOpen_one: '1 item open',
    todayOpen_other: '{{count}} items open',
    toList: 'To the list',
  },
  bucket: {
    heading: 'Bucket list',
    intro:
      'What you want to experience — not tasks. Unless shared, only you see an entry. “Plan” puts it in the calendar; afterwards the app asks whether you did it.',
    title: 'What do you want to experience?',
    add: 'Save',
    shareNew: 'Share with household',
    ideas: 'Ideas',
    noIdeas: 'None yet. What have you always wanted to do?',
    planned: 'Planned',
    plannedFor: 'Planned for {{day}}',
    doneHeading: 'Done',
    done: 'Done it',
    backToIdea: 'Back to ideas',
    openDay: 'Open day',
    shared: 'with household',
    private: 'just you',
    plan: 'Plan',
    planNamed: 'Plan {{name}}',
    remove: 'Remove {{name}}',
    share: 'Share with household',
    makePrivate: 'Just for me',
    toCalendar: 'Add to calendar',
    askHeading: 'Did it?',
    doneAndWrite: 'Yes — add a photo or thought',
    notYet: 'Not yet',
    story: 'Done: {{title}}',
  },
  diary: {
    storyHeading: 'What happened',
    training: 'Training: {{title}}',
    minutes_one: '1 minute',
    minutes_other: '{{count}} minutes',
    sets_one: '1 set',
    sets_other: '{{count}} sets',
    food: 'Eaten: {{kcal}} kcal, {{protein}} g protein',
    proteinReached: 'protein target reached',
    water: 'Drunk: {{liters}} l',
    tasksDone_one: 'Done: {{list}}',
    tasksDone_other: '{{count}} done: {{list}}',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    moodValue: 'mood {{value}} of 5',
    energyValue: 'energy {{value}} of 5',
    noMood: 'no mood entered',
    trained: 'trained',
    written: 'photos or thoughts',
    legend:
      'Fill: mood (otherwise energy), stronger is better. Dot: training. Ring: photos or thoughts.',
  },
  water: {
    heading: 'Water',
    onTrack: 'On track for this time of day.',
    dueBy: 'About {{ml}} ml more would be due by now.',
    plus: '+ {{ml}} ml',
    glassNamed: 'Log a glass ({{ml}} ml)',
    bottleNamed: 'Log a bottle ({{ml}} ml)',
    other: 'Other',
    undoNamed: 'Undo the last {{ml}} ml',
    amount: 'Amount (ml)',
    add: 'Add',
    changeTarget: 'Change daily target',
    settingsHeading: 'Water',
    target: 'Daily target (ml)',
    targetSource:
      'Default 1500 ml: the German Nutrition Society recommends about 1.5 litres of fluids a day for adults — more with exercise and heat.',
    midday: 'Ask at midday how much I’ve had',
    pace: 'Remind me when I’m behind (11:00, 15:00, 18:00)',
    remindersWhy:
      'Each notification comes once and doesn’t nag. The midday question stays silent once you’ve reached the target; the reminder only comes when you’re at least a glass behind the target spread over the day.',
  },
  meals: {
    heading: 'Adjust meals',
    intro:
      'Time and reminder per meal. With a time, the day is sorted by time — training and supplements then sit in their place too. Without a time, you set the order with the arrows.',
    introChild:
      'Time and reminder per meal. With a time, the day is sorted by time — training then sits in its place too. Without a time, you set the order with the arrows.',
    up: 'Move {{name}} up',
    down: 'Move {{name}} down',
    remove: 'Remove {{name}}',
    time: 'Time',
    timeNamed: 'Time for {{name}}',
    noTime: 'No reminder without a time — order with the arrows.',
    byTime: 'Sorted by its time in the day.',
    workoutNote:
      'Always sit right before and after training and are hidden on rest days. For a fixed time, add a snack.',
    reminderOn: 'Reminder on',
    reminderOff: 'Reminder off',
    reminderNamed: 'Reminder for {{name}}',
    addSnack_one: 'Add a snack (1 left)',
    addSnack_other: 'Add a snack ({{count}} left)',
    snacksFull: 'Three extra snacks is the maximum.',
    atWithReminder: '{{time}} · with reminder',
    skip: 'Skip',
    skipNamed: 'Skip {{name}} on this day',
    skipped: 'Skipped on this day',
    unskip: 'Undo',
    unskipNamed: 'Don’t skip {{name}}',
    training: 'Workout',
    trainingNoPlan: 'no plan day',
    trainingDuration: '{{minutes}} min',
    trainingDone: 'done',
  },
  modules: {
    heading: 'What I use',
    intro: 'Anything switched off disappears from the bar and from “Today” and stops reminding.',
    keep: 'Your data stays — switching it back on brings everything back.',
    on: 'On',
    off: 'Off',
    confirm: 'Use these',
    name: {
      training: 'Training',
      nutrition: 'Nutrition',
      planning: 'Planning',
      household: 'Household',
      journal: 'Journal',
    },
    why: {
      training: 'Plans, sets, progress, training days with reminders.',
      nutrition: 'Meals, targets, meal plan, supplements with reminders.',
      planning: 'Calendar, events, tasks — for kids too.',
      household: 'Shopping list, pantry, costs and members, shared at home.',
      journal: 'Morning check-in, thoughts, photos, review.',
    },
  },
  setup: {
    progress: 'Setup · {{done}} of {{total}}',
    tileHeading: 'Ready in a few minutes',
    tileIntro:
      'Kraftwerk needs a few details to remind, calculate and plan for you. You can skip any step.',
    continue: 'Next: {{step}}',
    hide: 'Hide',
    hideHint: 'You can reopen it in the settings.',
    doneLabel: '(done)',
    openLabel: '(open)',
    stepOf: 'Step {{step}} of {{total}}',
    optional: 'Optional',
    stepDone: 'Done.',
    back: 'Back',
    next: 'Next',
    skip: 'Skip',
    finish: 'Finish',
    reopen: 'Open setup',
    reopenIntro: 'Go through the first steps again — install, reminders, targets, training.',
    step: {
      modules: {
        short: 'What you use',
        title: 'What do you use?',
        why: 'Pick the areas you need. The rest stays out of your way — and can be switched on in the settings any time.',
      },
      install: {
        short: 'Add to home screen',
        title: 'Add to home screen',
        why: 'On iPhone, reminders only arrive when Kraftwerk is installed like an app.',
      },
      push: {
        short: 'Allow reminders',
        title: 'Allow reminders',
        why: 'Kraftwerk reminds you about supplements, training and rest periods — and follows up until you tick it off.',
      },
      body: {
        short: 'Body and goal',
        title: 'Body and goal',
        why: 'From height, weight, age, daily activity and goal the app suggests your targets. Nothing is saved until you tap.',
      },
      training: {
        short: 'Plan training',
        title: 'Plan training',
        why: 'Pick a plan and the days you train. Reminders and weekly rings come from this.',
      },
      more: {
        title: 'Set up more',
        why: 'Everything here is optional and can be done any time.',
      },
    },
    install: {
      already: 'Kraftwerk already runs from the home screen.',
      button: 'Install',
      ios1: 'Tap the share icon at the bottom of Safari (square with an arrow pointing up).',
      ios2: 'Choose “Add to Home Screen” and tap “Add”.',
      ios3: 'From now on, open Kraftwerk from the new icon on your home screen.',
      relogin: 'You may need to sign in once more there.',
    },
    training: {
      plan: 'Plan',
      noPlans: 'No plan yet.',
      own: 'Create your own plan',
    },
    more: {
      supplements: {
        title: 'Supplements',
        why: 'What you take, when and with which meal — with a reminder until it is ticked off.',
        action: 'Set up supplements',
      },
      household: {
        title: 'Household',
        why: 'Share the shopping list, pantry, events and tasks with the people you live with.',
        action: 'Set up household',
      },
      calendar: {
        title: 'Connect calendar',
        why: 'See events from Apple or Google in Kraftwerk — and Kraftwerk in your own calendar.',
        action: 'Connect calendar',
      },
    },
  },
  journalLock: {
    heading: 'Journal lock',
    intro:
      'The app asks for your device lock (Face ID, fingerprint or PIN) before opening the journal — on this device. It keeps prying eyes out, not someone reading out the device.',
    on: 'Lock on',
    off: 'Lock off',
    unsupported: 'This browser cannot use passkeys — the lock is not available here.',
    needPasskey: 'This device needs a passkey for your account (see “Passkeys” further down).',
    locked: 'Journal locked',
    lockedHint:
      'Unlock with your device lock. After five minutes or once the app was in the background, it asks again.',
    unlock: 'Unlock',
    usePassword: 'Unlock with password',
    password: 'Password',
    unlockPassword: 'Check password',
    wrongPassword: 'The password is wrong.',
    tooMany: 'Too many attempts. Please try again later.',
    offline: 'The password can only be checked while connected to the server.',
    result: {
      cancelled: 'Cancelled — or there is no passkey on this device.',
      notVerified: 'The device did not ask for Face ID, fingerprint or PIN.',
      otherAccount: 'This passkey belongs to a different account.',
    },
  },
  notes: {
    heading: 'Thoughts',
    add: 'Capture a thought',
    empty: 'Nothing captured on this day yet.',
    edit: 'Edit',
    title: 'Heading (optional)',
    time: 'Time',
    body: "What's on your mind",
    placeholder: 'Just start writing — you can dictate via the keyboard microphone.',
    tagsHint: 'Use #word for tags, like #holiday or #family.',
    mood: 'Mood (optional)',
    moodValue: 'Mood {{value}}/5',
    event: 'About an event',
    noEvent: 'None',
    about: 'About: {{title}} · {{date}}',
    save: 'Save',
    delete: 'Delete thought',
    deleteWarning: 'The thought and its photos will be deleted.',
    count_one: '1 thought',
    count_other: '{{count}} thoughts',
    search: 'Search thoughts',
    searchShort: 'Search thoughts …',
    searchHint: 'Words or #tag',
    noResults: 'Nothing found.',
    memories: 'On this day',
    monthAgo: 'A month ago',
    yearsAgo_one: 'A year ago',
    yearsAgo_other: '{{count}} years ago',
    writeAbout: 'Write about this',
    hasNote: '✎ thought',
  },
  journal: {
    checkinHeading: 'How you felt',
    nothingEntered: 'Nothing entered.',
    addLater: 'Add',
    edit: 'Edit',
    doneEditing: 'Done',
    scaleRather: 'fairly {{word}}',
    scaleMiddle: 'medium',
    scaleValue: '{{word}} ({{value}}/5)',
    notEnteredList: 'Not entered: {{list}}',
    weight: 'Weight',
    weightHint: 'e.g. 82.5',
    weightInvalid: 'That does not look like kilograms (20–400).',
    weightTrend: 'Weight',
    weightAvg: 'kg average over the last 7 days',
    weightChange: '{{change}} kg vs. the week before',
    weightTooFew: 'A trend needs at least {{min}} weigh-ins per week.',
    weightLatest: 'Last {{kg}} kg on {{date}}',
    steps: {
      value: '{{steps}} steps',
      source: 'from Apple Health',
    },
    measurements: {
      toggle: 'Add body measurements',
      toggleCount: 'Body measurements ({{count}} taken)',
      how: 'In the morning, before eating, tape on bare skin — always at the same spot. Empty means not measured.',
      waist: 'Waist',
      hips: 'Hips',
      chest: 'Chest',
      upperArm: 'Upper arm',
      thigh: 'Thigh',
      bodyFat: 'Body fat',
      invalid: 'Please between {{min}} and {{max}} {{unit}}.',
      value: { cm: '{{value}} cm', '%': '{{value}} %' },
      trend: 'Body measurements',
      latest: '{{value}} on {{date}}',
      change: '{{change}} since {{date}}',
      single: 'first value',
      measured: 'Measurements taken',
    },
    more: 'Add more — mood, note, photos',
    back: 'Back',
    today: 'Today',
    days: {
      checkin: 'Check-in',
      note: 'Thought',
      photo: 'Photo',
      noCheckin: 'No check-in yet.',
      thisWeek: 'This week',
      lastWeek: 'Last week',
      weekRange: '{{from}} – {{to}}',
      emptyOne: '{{day}}: nothing entered',
      emptyRun: '{{from}} to {{to}} · {{count}} days with nothing entered',
      older: 'Older weeks',
      bucket: 'Done: {{title}}',
      training: 'Training: {{list}}',
      kcal: '{{kcal}} kcal',
      water: '{{liters}} l water',
      events_one: '1 event',
      events_other: '{{count}} events',
    },
    noPhotos: 'No photos yet.',
    openDay: 'Open day: {{day}}',
    tabs: {
      label: 'Journal',
      days: 'Days',
      month: 'Month',
      bucket: 'Bucket list',
      review: 'Review',
      photos: 'Photos',
      week: 'Week',
    },
    summary: {
      weight: '{{kg}} kg',
      sleep: 'sleep {{hours}} h',
      energy: 'energy {{value}}/5',
      mood: 'mood {{value}}/5',
      soreness: 'soreness {{value}}/5',
      photos_one: '1 photo',
      photos_other: '{{count}} photos',
    },
    heading: 'How are you today?',
    sleepHours: 'Sleep last night',
    sleepLess: 'Half an hour less sleep',
    sleepMore: 'Half an hour more sleep',
    notEntered: 'not entered',
    hours: '{{hours}} h',
    sleepQuality: 'Sleep quality',
    energy: 'Energy',
    mood: 'Mood',
    soreness: 'Muscle soreness',
    note: 'Note',
    scale: {
      bad: 'bad',
      good: 'good',
      low: 'drained',
      high: 'full of it',
      strong: 'strong',
      none: 'none',
    },
    photos: {
      saveToFiles: 'Save to Files',
      saved: 'Saved',
      heading: 'Photos',
      add: 'Add photo',
      preparing: 'Shrinking …',
      hint: 'Shrunk on the device (1600 px). The location is removed in the process.',
      failed: 'The photo could not be processed.',
      waiting: 'Waiting to upload',
      uploadFailed: 'Upload failed ({{reason}}) — still on this device',
      elsewhere: 'Not uploaded yet — it is on another device',
      alt: 'Journal photo',
      remove: 'Remove',
    },
    rating: {
      question:
        'How did it go? Weak is what you rate as weak — the app does not infer it from the weights.',
      label: 'How did it go?',
      low: 'weak',
      high: 'strong',
      skip: 'Finish without rating',
      shown: 'Rated: {{rating}} of 5',
      none: 'Not rated',
      rate: 'Rate',
      change: 'Change',
    },
    context: {
      before: 'Before:',
      meals: '24 h: {{kcal}} kcal, {{protein}} g protein',
      noMeals: '24 h: nothing logged',
      lastMealMinutes: 'last meal {{minutes}} min before',
      lastMeal: 'last meal {{hours}} h {{minutes}} min before',
      sleep: 'sleep {{hours}} h',
      noSleep: 'sleep not entered',
      energy: 'energy {{energy}}/5',
    },
  },
  settings: {
    jump: 'Settings sections',
    groups: {
      reminders: 'Reminders',
      me: 'Me and goals',
      connections: 'Connections',
      privacy: 'Privacy',
      look: 'Appearance',
      account: 'Access and data',
    },
    review: {
      heading: 'Weekly review',
      intro:
        'On Sundays from 18:00, a notification with the week’s numbers: protein, training, supplements, weight. Tapping opens the week view in the journal.',
      on: 'Notify on Sundays: on',
      off: 'Notify on Sundays: off',
    },
    heading: 'Settings',
    close: 'Close',
    prep: 'Evening reminder',
    look: 'Appearance',
    logoutPhotos_one:
      '1 photo hasn’t been uploaded yet and will be lost when you sign out. Sync with a connection first?',
    logoutPhotos_other:
      '{{count}} photos haven’t been uploaded yet and will be lost when you sign out. Sync with a connection first?',
    logoutEstimates_one:
      '1 photo for estimating hasn’t reached the server yet and will be lost when you sign out. Sync with a connection first?',
    logoutEstimates_other:
      '{{count}} photos for estimating haven’t reached the server yet and will be lost when you sign out. Sync with a connection first?',
    logoutPending_one:
      '1 change hasn’t reached the server yet and will be lost when you sign out. Sync with a connection first?',
    logoutPending_other:
      '{{count}} changes haven’t reached the server yet and will be lost when you sign out. Sync with a connection first?',
    logoutRetry: 'Sync again',
    logoutChecking: 'Syncing …',
    logoutAnyway: 'Sign out anyway',
    export: {
      action: 'Export my data',
      busy: 'Collecting …',
      hint: 'Everything the server knows about your account, as a file. Photos kept only on this device are saved via “Save to Files”.',
      done: 'Export created.',
      failed: 'Export failed — it needs a connection to the server.',
    },
    delete: {
      action: 'Delete account',
      warning:
        'Deletes your account with all data — supplements, training, nutrition, journal, photos. This cannot be undone. Export first?',
      shared:
        'Anything you put on a shared household’s shopping list or pantry stays for the others.',
      password: 'Password to confirm',
      confirm: 'Delete permanently',
      busy: 'Deleting …',
      wrongPassword: 'Wrong password. Nothing was deleted.',
      failed: 'Deletion failed — nothing was deleted. Are you connected?',
    },
  },
  prep: {
    heading: 'For tomorrow',
    nothing: 'Nothing to prepare for tomorrow, and nothing is missing.',
    missing: 'Missing at home',
    missingGrams: '{{grams}} g missing',
    missingCheck: 'check — amount open',
    unknownIngredients: 'No ingredient list, so not checked: {{recipes}}.',
    done: 'Done',
    doneAlready: 'Done for tonight.',
    reminderOff:
      'Evening reminder is off. When on, the phone speaks up the evening before — only if something needs preparing or is missing.',
    reminderOn: 'Evening reminder at {{time}}. If there is nothing to do, the phone stays quiet.',
    time: 'Time',
    enable: 'Turn on',
    change: 'Change',
    disable: 'Turn off',
    tooLate: 'No later than {{time}} — after that the reminder would expire before it is sent.',
  },
  shopping: {
    fromPlan: 'Add from the meal plan',
    noHousehold: 'No household on this device yet — please sync once while online.',
    amountUnknown: 'amount open',
    amountUnknownShort: 'open',
    unit: { g: 'g', ml: 'ml', piece: 'pcs' },
    unitFormat: { g: '{{amount}} g', ml: '{{amount}} ml', piece: '{{amount}} pcs' },
    category: {
      produce: 'Fruit & veg',
      bakery: 'Bread & bakery',
      chilled: 'Chilled',
      frozen: 'Frozen',
      dry: 'Dry goods',
      canned: 'Tins & jars',
      spices: 'Spices & oils',
      drinks: 'Drinks',
      household: 'Household',
      other: 'Other',
    },
    tabs: {
      label: 'Shopping',
      list: 'List',
      pantry: 'Pantry',
      costs: 'Costs',
      pocket: 'Pocket money',
      members: 'Members',
    },
    item: {
      fromLibrary: 'From the library',
      name: 'What',
      amount: 'Amount',
      unit: 'Unit',
      category: 'Aisle',
      details: 'Amount and aisle',
    },
    list: {
      heading: 'Shopping list',
      open: 'open',
      add: 'Add',
      empty: 'Nothing open. Add items by hand or from the meal plan.',
      fromPlan: 'from the plan',
      remove: 'Remove {{name}}',
      done_one: '1 checked',
      done_other: '{{count}} checked',
      finish_one: 'Finish shopping · 1 to pantry',
      finish_other: 'Finish shopping · {{count}} to pantry',
      finishHint:
        'Checked items go to the pantry and leave the list. Same item in the same unit is added up.',
      finished_one: '1 item moved to the pantry.',
      finished_other: '{{count}} items moved to the pantry.',
    },
    consumption: {
      heading: 'Due again soon',
      intro:
        'From what you have checked off so far — from three purchases on, worked out on this device.',
      every_one: 'bought about daily',
      every_other: 'bought about every {{count}} days',
      since_one: 'last yesterday',
      since_other: 'last {{count}} days ago',
      uncertain_one: 'still uncertain, only 1 purchase',
      uncertain_other: 'still uncertain, only {{count}} purchases',
      add: 'Add to list',
      addNamed: 'Add {{name}} to the list',
      hide: 'Hide',
      hideNamed: 'Do not suggest {{name}} until the next purchase',
      added: '{{name}} is on the list.',
    },
    plan: {
      heading: 'From the meal plan',
      intro:
        'What your planned meals need, minus pantry and minus what is already on the list. Calculated in grams from the ingredients.',
      from: 'From',
      to: 'To',
      empty: 'Nothing planned in this period.',
      missingRefs_one:
        '1 plan entry points to a deleted meal or food and is missing from the calculation.',
      missingRefs_other:
        '{{count}} plan entries point to deleted meals or foods and are missing from the calculation.',
      needed: 'need {{grams}} g',
      neededUnknown: 'need: amount in plan unknown',
      inPantry: 'pantry {{grams}} g',
      inPantryUnknown: 'in pantry, amount open',
      toBuy: 'missing {{grams}} g',
      for: 'for {{recipes}}',
      add_one: 'Add 1 to the list',
      add_other: 'Add {{count}} to the list',
      unstructuredHeading: 'Meals without an ingredient list',
      unstructuredIntro:
        'These meals have no ingredients in grams — the app cannot calculate and does not guess. Tap a part to put it on the list as written.',
      times_one: 'planned 1×',
      times_other: 'planned {{count}}×',
      noItems: 'No ingredients entered — please add them to the meal.',
      quickAdd: 'Put {{text}} on the list',
    },
    status: {
      buy: 'buy',
      covered: 'in pantry',
      listed: 'on list',
      check: 'check',
    },
    pantry: {
      heading: 'Pantry',
      intro:
        'What is at home. With an amount in grams it is subtracted from the need; without one the list says “check”. Cooking does not deduct automatically.',
      add: 'Add to pantry',
      empty: 'Nothing in the pantry yet.',
      amountFor: 'Amount of {{name}}',
      remove: 'Remove {{name}} from the pantry',
    },
    household: {
      heading: 'Household',
      intro:
        'You see the shopping list, pantry and whatever you share (events, tasks, costs) with everyone here. Training, nutrition and journal stay private.',
      inviteHeading: 'Invite someone',
      inviteIntro: 'Creates a code, valid for seven days, usable once. Send it any way you like.',
      invite: 'Create code',
      inviteAgain: 'Create new code',
      inviteExpires: 'Valid until {{date}}',
      joinHeading: 'Join a household',
      joinIntro:
        'If you are the only adult here, everything shared comes with you — your children too. Otherwise it stays with the others.',
      code: 'Invite code',
      join: 'Join',
      joined_one: 'Joined. 1 item came along.',
      joined_other: 'Joined. {{count}} items came along.',
      leaveHeading: 'Leave',
      leaveIntro: 'Everything shared stays with the others. You get a new, empty household.',
      leaveAlone: 'You are alone in this household — leaving would orphan the list and pantry.',
      leaveLastAdult:
        'You are the only adult here — the children would be left alone. Invite someone first, or join another household: they come with you.',
      leave: 'Leave',
      leaveConfirm: 'Really leave',
      codeInvalid: 'The code is invalid, expired or already used.',
      conflict: 'Not possible right now — you are already a member or alone in the household.',
      tooMany: 'Too many attempts. Please try again in a few minutes.',
      offline: 'No connection to the server. Changing the household only works online.',
    },
  },
  householdMeals: {
    heading: 'Meals in the household',
    intro:
      'What the household eats. Everyone eats along by default, one portion each — change it here or in detail in your own day under Nutrition.',
    loading: 'Loading …',
    noHousehold: 'No household on this device yet — it arrives with the first sync.',
    weekOf: 'Week from {{date}}',
    prevWeek: 'Previous week',
    nextWeek: 'Next week',
    thisWeek: 'This week',
    dayEmpty: 'Nothing planned.',
    add: 'Add',
    addNamed: 'Add a dish for {{day}}',
    perPortion: '{{kcal}} kcal · {{protein}} g protein per portion',
    noValues: 'Nutrition unknown — counts in no balance',
    source: 'Source: {{source}}',
    cooks: 'Cooking: {{name}}',
    eaters: 'Who eats along · {{amount}} portions of the dish',
    me: 'Me',
    unknownMember: 'Unknown',
    portions_one: '{{amount}} portion',
    portions_other: '{{amount}} portions',
    notEating: 'not eating',
    variant: 'own variant: {{name}}',
    extras_one: '{{count}} extra',
    extras_other: '{{count}} extras',
    vegetarian: 'vegetarian',
    vegetarianSelf: 'You eat vegetarian — pick your own variant in your day under Nutrition.',
    eats: 'Eats along',
    notEatingShort: 'Not eating',
    meEatsNamed: 'I eat along for {{name}}',
    eatsNamed: '{{name}} eats along for {{dish}}',
    portionFor: 'Portion for {{name}}',
    removeNamed: 'Remove {{name}} from the plan',
    toShopping: 'To the shopping list',
    toShoppingHint:
      '“Add from the meal plan” on the shopping list counts every portion of everyone eating along, their extras and variants — minus the pantry.',
    form: {
      recipe: 'Own dish',
      free: 'Just a name',
      slot: 'Meal',
      pickRecipe: 'Dish',
      choose: 'Please choose',
      name: 'Name',
      note: 'Note',
      cook: 'Who cooks',
      nobody: 'Not decided',
      frozenHint:
        'The dish is frozen: name, ingredients and nutrition stay as they are now. Later changes to the recipe do not follow.',
      save: 'Add',
      cancel: 'Cancel',
    },
    wishes: {
      heading: 'Wishes',
      intro: 'What someone would like. Adults take it into the plan.',
      empty: 'No wishes right now.',
      name: 'What would you like?',
      note: 'Note',
      save: 'Wish for it',
      from: 'from {{name}}',
      take: 'Into the plan',
      takeNamed: 'Take {{name}} into the plan',
      takeWith: 'With an own dish (ingredients and nutrition)',
      takeNoRecipe: 'Just the name',
      day: 'Day',
      decline: 'Decline',
      declineNamed: 'Decline the wish {{name}}',
      removeOwn: 'Take back',
      removeOwnNamed: 'Take back the wish {{name}}',
      smallChild: 'Your parents add wishes for you.',
    },
    day: {
      kicker: 'From the household plan',
      counted: '{{amount}}× · {{kcal}} kcal · {{protein}} g',
      noValues: 'does not count — nutrition unknown',
      notEating: 'You are not eating along',
      fromRule: 'portion from your standing rule',
      instead: 'instead of {{name}}',
      skip: 'Not eating',
      skipNamed: 'Do not eat along for {{name}}',
      eatAgain: 'Eat along',
      eatAgainNamed: 'Eat along for {{name}} after all',
      portion: 'Portion',
      portionNamed: 'Change my portion of {{name}}',
      portionLabel: 'My portion (factor)',
      portionSave: 'Apply',
      portionReset: 'Same as everyone',
      extra: 'Extra',
      extraNamed: 'Extra with {{name}}',
      variant: 'Own variant',
      variantNamed: 'Own variant instead of {{name}}',
      variantRemove: 'Undo variant',
      extraGrams: '{{grams}} g',
      extraPortions_one: '{{portions}} serving',
      extraPortions_other: '{{portions}} servings',
      extraValues: '{{kcal}} kcal · {{protein}} g protein',
      removeExtraNamed: 'Remove extra {{name}}',
      pickFood: 'Food',
      pickRecipe: 'Dish',
      choose: 'Please choose',
      grams: 'Amount (g)',
      portions: 'Portions',
      add: 'Add',
      noRecipeValues:
        'This dish has no nutrition values — as an extra it would count as 0. Please add values first.',
      vegetarianHint: 'You eat vegetarian — an own variant helps here.',
      proteinGap:
        'With everything logged and planned you are still {{grams}} g short of your own protein target today.',
    },
    rules: {
      heading: 'Eating along in the household',
      intro:
        'Applies to every entry of the household plan until you say otherwise there. The household sees your portion, not your nutrition.',
      factor: 'Portion at {{meal}}',
      sameAsAll: 'Same as everyone (1)',
      trainingExtra: 'On training days, after training add',
      trainingNone: 'Nothing',
      vegetarian: 'Vegetarian instead of meat (a hint to the household)',
      save: 'Save',
      saved: 'Saved.',
    },
  },
};
